// Vanuatu National Curriculum - Primary Years 1-6
// Based on official syllabus documents
// Years 4-6 content imported from separate module

import {
  years4to6Outcomes,
  sampleActivitiesYears4to6,
  assessmentSamplesYears4to6,
  yearlyPlannerTemplatesYears4to6,
  schemeOfWorkTemplatesYears4to6,
  worksheetTemplatesYears4to6,
  lessonPlanTemplatesYears4to6,
  testBlueprintTemplatesYears4to6,
  testPapersYears4to6
} from './primaryCurriculumYears4to6';

export const primaryCurriculum = {
  // Language and Communication - Based on Vanuatu National Syllabus 2015 (Years 1-3)
  languageCommunication: {
    subject: "Language and Communication",
    description: "Children use language competently and critically, in oral and written forms, to communicate their thoughts, feelings, knowledge and understanding in vernacular and beginning English.",
    strands: [
      {
        id: "listening-speaking",
        title: "Listening and Speaking",
        color: "blue",
        subStrands: ["Skills and Strategies", "Production", "Context and Texts"],
        yearlyOutcomes: {
          "Beginning Year 1": [
            "LSSS B.1: Use basic skills of listening and speaking in vernacular",
            "LSP B.1: Listen and respond to simple instructions and greetings",
            "LSCT B.1: Be aware of using language differently in different situations"
          ],
          "Year 1": [
            "LSSS.1.V1: Demonstrate a range of listening and speaking skills in the classroom",
            "LSP.1.V1: Speak confidently and listen actively using vernacular in informal and formal situations in the classroom",
            "LSCT.1.V1: Recognise that language changes to suit particular situations at school"
          ],
          "Year 2": [
            "LSSS.2.V1: Use consistently, skills of listening and speaking in a range of familiar situations",
            "LSSS.2.V2: Identify and say clearly English letter sounds and combinations and develop relevant vocabulary",
            "LSP.2.V1: Listen and speak to others in informal and formal situations at school and in the community using vernacular",
            "LSP.2.V2: Answer simple questions and respond to familiar greetings and commands in English",
            "LSCT.2.V1: Adjust their speaking to suit a range of familiar situations at home, school and in the community"
          ],
          "Year 3": [
            "LSSS.3.V/E1: Develop appropriate vocabulary and pronunciation in English",
            "LSP.3.V/E1: Listen to, begin to speak and attempt to use appropriate vocabulary and pronunciation in English",
            "LSCT.3.V/E1: Demonstrate appropriate listening and speaking behaviour in a variety of situations"
          ],
          "Year 4": [
            "LSSS.4.1: Demonstrate listening and speaking skills and strategies to communicate for familiar purposes and situations",
            "LSP.4.1: Listen, understand, speak and interact for a range of familiar purposes and situations",
            "LSCT.4.1: Demonstrate how spoken language changes to suit a range of familiar purposes and situations"
          ],
          "Year 5": [
            "LSSS.5.1: Use listening and speaking skills and strategies to communicate for a variety of purposes and audiences",
            "LSP.5.1: Listen, speak and interact in formal and informal situations for a variety of purposes and audiences",
            "LSCT.5.1: Compare how spoken language changes to suit a range of purposes and audiences"
          ],
          "Year 6": [
            "LSSS.6.1: Apply listening and speaking skills and strategies to communicate effectively for different purposes, situations and audiences",
            "LSP.6.1: Listen and communicate clearly for a range of purposes, situations and audiences",
            "LSCT.6.1: Adapt spoken language to suit different purposes, situations and audiences"
          ]
        }
      },
      {
        id: "reading",
        title: "Reading",
        color: "green",
        subStrands: ["Skills and Strategies", "Production", "Context and Texts"],
        yearlyOutcomes: {
          "Beginning Year 1": [
            "RSS B.1: Use pre-reading skills to handle books and identify pictures",
            "RP B.1: Demonstrate pre-reading behaviour when interacting with texts",
            "RCT B.1: Recognize that pictures and words convey meaning"
          ],
          "Year 1": [
            "RSS.1.V1: Develop skills and strategies to read and understand familiar vernacular texts",
            "RP.1.V1: Read and respond to simple vernacular texts",
            "RCT.1.V1: Recognise that texts can be imaginary and factual"
          ],
          "Year 2": [
            "RSS.2.V1: Use simple skills and strategies to read and understand familiar vernacular texts",
            "RP.2.V1: Read and understand simple vernacular texts",
            "RCT.2.V1: Describe the purposes of familiar imaginary and factual texts"
          ],
          "Year 3": [
            "RSS.3.V/E1: Select and apply a variety of skills and strategies to read and understand simple English texts",
            "RP.3.V/E1: Read and comprehend simple graded texts in English",
            "RCT.3.V/E1: Recognise that texts are written for different purposes"
          ],
          "Year 4": [
            "RSS.4.1: Demonstrate a range of skills and strategies to read and understand familiar English texts",
            "RP.4.1: Read and understand a range of familiar texts in English",
            "RCT.4.1: Identify familiar text types used for different purposes"
          ],
          "Year 5": [
            "RSS.5.1: Apply a wide range of skills and strategies to read and understand fiction and non-fiction texts",
            "RP.5.1: Read with understanding a range of fiction and non-fiction texts",
            "RCT.5.1: Discuss how different text types are used for different purposes and situations"
          ],
          "Year 6": [
            "RSS.6.1: Select and apply suitable skills and strategies to read and understand fiction and non-fiction texts",
            "RP.6.1: Read with understanding a wide range of more complex fiction and non-fiction texts",
            "RCT.6.1: Analyse how language changes in different text types to suit the purpose and audience"
          ]
        }
      },
      {
        id: "writing",
        title: "Writing",
        color: "purple",
        subStrands: ["Skills and Strategies", "Production", "Context and Texts"],
        yearlyOutcomes: {
          "Beginning Year 1": [
            "WSS B.1: Experiment with pre-writing skills and holding a pencil",
            "WP B.1: Represent ideas using pictures and dictation",
            "WCT B.1: Recognize that writing and symbols are used to record ideas"
          ],
          "Year 1": [
            "WSS.1.V1: Develop beginning handwriting skills",
            "WSS.1.V2: Use basic rules of writing to construct simple vernacular texts",
            "WP.1.V1: Write sentences in their vernacular",
            "WCT.1.V1: Recognise that writing is used for different purposes"
          ],
          "Year 2": [
            "WSS.2.V1: Demonstrate the basic mechanics of handwriting",
            "WSS.2.V2: Use appropriate rules to organise writing",
            "WP.2.V1: Write simple texts in their vernacular",
            "WCT.2.V1: Identify a range of purposes for writing"
          ],
          "Year 3": [
            "WSS.3.V/E1: Write using print script and transition to using cursive",
            "WSS.3.V/E2: Use basic rules of writing to construct simple English texts",
            "WP.3.V/E1: Write sentences to create simple texts in English",
            "WCT.3.V/E1: Identify when and how to use writing for different purposes"
          ],
          "Year 4": [
            "WSS.4.1: Identify and use punctuation, grammar, language rules and the writing process",
            "WSS.4.2: Apply handwriting techniques to write fluently and legibly",
            "WP.4.1: Write sentences and paragraphs to create a range of simple texts",
            "WCT.4.1: Discuss how own texts differ according to their purpose and audience"
          ],
          "Year 5": [
            "WSS.5.1: Demonstrate language knowledge, skills, strategies and the writing process",
            "WP.5.1: Write a range of fiction and non-fiction texts",
            "WCT.5.1: Explain how written language changes to suit the purpose and audience"
          ],
          "Year 6": [
            "WSS.6.1: Apply language knowledge, skills, strategies and the writing process effectively",
            "WP.6.1: Create a range of more complex fiction and non-fiction texts",
            "WCT.6.1: Adapt written language for a variety of purposes and audiences"
          ]
        }
      },
      {
        id: "viewing",
        title: "Viewing",
        color: "orange",
        subStrands: ["Skills and Strategies", "Production", "Context and Texts"],
        yearlyOutcomes: {
          "Beginning Year 1": [
            "VSS B.1: Identify simple cultural signs and symbols in the environment",
            "VP B.1: Make signs and symbols to share simple messages",
            "VCT B.1: Talk about how signs and symbols are used in the community"
          ],
          "Year 1": [
            "VSS.1.V1: Share messages using signs and symbols and visual texts",
            "VP.1.V1: Express their personal thoughts and messages using signs, symbols and visual texts",
            "VCT.1.V1: Recognise when and where some signs, symbols and visual texts are used in the community to convey special messages"
          ],
          "Year 2": [
            "VSS.2.V1: Identify and interpret the messages conveyed by signs, symbols and visual texts in the classroom and their environment",
            "VP.2.V1: Produce simple signs, symbols and visual texts for specific purposes",
            "VCT.2.V1: Investigate different purposes for using signs, symbols and visual texts"
          ],
          "Year 3": [
            "VSS.3.V/E1: Identify the key characteristics of signs, symbols and visual texts",
            "VP.3.V/E1: Create a range of signs, symbols and visual texts using key characteristics",
            "VCT.3.V/E1: Explain when, where and why different signs, symbols and visual texts are used"
          ],
          "Year 4": [
            "VSS.4.1: Identify key features and techniques in simple visual texts",
            "VP.4.1: View, interpret and create simple visual texts",
            "VCT.4.1: Discuss the purposes and audiences for simple visual texts"
          ],
          "Year 5": [
            "VSS.5.1: Demonstrate how different features and techniques are used to communicate meaning in visual texts",
            "VP.5.1: View, interpret and create a range of visual texts",
            "VCT.5.1: Discuss and compare how a range of visual texts is used for different purposes and audiences"
          ],
          "Year 6": [
            "VSS.6.1: Adapt and use features and techniques to interpret and create meaning in a range of visual texts",
            "VP.6.1: Analyse, interpret and create a range of visual texts",
            "VCT.6.1: Select and use appropriate visual texts for different purposes and audiences"
          ]
        }
      }
    ]
  },

  // Mathematics - Based on Vanuatu National Syllabus 2015 (Years 1-3)
  mathematics: {
    subject: "Mathematics",
    description: "Children demonstrate mathematical knowledge, skills and attitudes needed in everyday life in their community and for further learning.",
    strands: [
      {
        id: "number",
        title: "Number and Operations",
        color: "blue",
        subStrands: ["Whole Numbers", "Operations", "Fractions"],
        yearlyOutcomes: {
          "Beginning Year 1": [
            "Count objects using vernacular number system",
            "Explore putting together, taking away, grouping, and sharing",
            "Recognize objects that can be broken down into parts"
          ],
          "Year 1": [
            "NWN.1.V1: Count, write numerals, compose, compare and order groups of objects up to 19",
            "NO.1.V1: Solve problems involving addition and subtraction of single digit whole numbers using concrete materials",
            "NF.1.V1: Break up objects into pieces and recompose them"
          ],
          "Year 2": [
            "NWN.2.V1: Read, write, represent and order whole numbers to 99 using a variety of materials",
            "NO.2.V1: Solve problems involving addition and subtraction of one and two digit whole numbers using a variety of strategies",
            "NF.2.V1: Recognise different ways of making a whole and parts of a whole"
          ],
          "Year 3": [
            "NWN.3.V/E1: Read, write, represent, compare, order, compose and decompose whole numbers from 0 to 999",
            "NO.3.V/E1: Solve problems using adding, subtracting and multiplying in a variety of ways",
            "NF.3.V/E1: Divide whole collections of objects into equal parts"
          ],
          "Year 4": [
            "NWN.4.1: Read, represent, compare and order whole numbers up to 10 000",
            "NO.4.1: Solve word and number problems using addition and subtraction up to 4-digit numbers",
            "NO.4.2: Solve word and number problems involving multiplication of 2- and 3-digit numbers by 1-digit numbers",
            "NO.4.3: Solve word and number problems involving division of up to 3-digit numbers by 1-digit numbers",
            "NO.4.4: Solve simple mental calculations",
            "NFD.4.1: Demonstrate understanding of simple fractions and ratios"
          ],
          "Year 5": [
            "NWN.5.1: Read, represent, compare and order whole numbers up to 100 000",
            "NO.5.1: Solve word and number problems using addition and subtraction up to 5-digit numbers",
            "NO.5.2: Solve word and number problems involving multiplication of 2- and 3-digit numbers by 1- and 2-digit numbers",
            "NO.5.4: Solve more complex mental calculations",
            "NFD.5.1: Solve problems related to fractions, decimals and ratios"
          ],
          "Year 6": [
            "NWN.6.1: Read, represent, compare and order whole numbers up to at least 1 000 000",
            "NO.6.1: Solve word and number problems using addition and subtraction up to 6-digit numbers",
            "NO.6.2: Solve word and number problems involving multiplication of 4- and 5-digit numbers by up to 3-digit numbers",
            "NO.6.3: Solve word and number problems involving division of up to 5-digit numbers by 1- or 2-digit numbers",
            "NO.6.4: Solve mental calculations by applying a range of strategies",
            "NFD.6.1: Solve problems related to fractions, decimals, ratios and percentage"
          ]
        }
      },
      {
        id: "measurement",
        title: "Measurement",
        color: "green",
        subStrands: ["Length, Weight and Capacity", "Time", "Money"],
        yearlyOutcomes: {
          "Beginning Year 1": [
            "Measure length, weight, and capacity using local methods",
            "Describe and predict daily routines using local time markers",
            "Identify and imitate traditional bartering methods"
          ],
          "Year 1": [
            "MLWC.1.V1: Measure and compare length, weight and capacity using non-standard methods",
            "MT.1.V1: Tell time in traditional ways and estimate duration of a variety of activities and events",
            "MM.1.V1: Identify and describe the coins 5, 10, 20, 50 and 100 vatu and use them to make various sums of money to purchase goods"
          ],
          "Year 2": [
            "MLWC.2.V1: Estimate, measure, compare and discuss length, weight and capacity using both non-standard and standard measurements",
            "MT.2.V1: Identify and sequence events that occur at different times in a day, a week and a year",
            "MM.2.V1: Use coins to solve simple problems and recognise notes (200, 500, 1000)"
          ],
          "Year 3": [
            "MLWC.3.V/E1: Estimate, measure, record and compare length, weight, height, distance, and capacity using standard units",
            "MT.3.V/E1: Recognize units of time by using a calendar and 12-hour clock (analogue)",
            "MM.3.V/E1: Use coins and notes to solve simple problems"
          ],
          "Year 4": [
            "ML.4.1: Estimate, measure, compare and solve simple problems relating to length and perimeter, using standard units",
            "MA.4.1: Estimate, measure and compare the area of squares and rectangles using non-standard and standard units",
            "MM.4.1: Estimate, measure, compare and solve simple problems relating to the mass of objects, using standard units",
            "MMCV.4.1: Estimate, measure, compare and solve simple problems relating to capacity and volume using standard units",
            "MMO.4.1: Estimate and solve real life problems involving changing money and giving change"
          ],
          "Year 5": [
            "ML.5.1: Estimate, measure, compare and solve problems relating to length and perimeter, using appropriate standard units",
            "MA.5.1: Estimate, measure, compare and solve problems relating to the area of squares, rectangles and triangles",
            "MM.5.1: Estimate, measure, compare and solve problems relating to the mass of objects, using appropriate standard units",
            "MMCV.5.1: Estimate, measure, compare and solve problems relating to the capacity and volume of objects, using appropriate standard units",
            "MT.5.1: Interpret calendars, convert units of time, estimate and read the time in 1-minute intervals",
            "MMO.5.1: Estimate and solve problems involving everyday calculations relating to purchasing and sales"
          ],
          "Year 6": [
            "ML.6.1: Apply estimation and measurement skills to practical problems involving length and perimeter",
            "MA.6.1: Apply estimation and measurement skills to practical problems involving area of quadrilaterals, triangles and complex shapes",
            "MM.6.1: Apply estimation and measurement skills to practical problems involving mass",
            "MMCV.6.1: Apply estimation and measurement skills to practical problems involving capacity and volume",
            "MT.6.1: Estimate and read time accurately in a range of ways and solve real life problems relating to time",
            "MMO.6.1: Estimate and solve real life problems relating to percentage of sums of money and banking"
          ]
        }
      },
      {
        id: "geometry",
        title: "Geometry and Spatial Sense",
        color: "purple",
        subStrands: ["Shapes and Angles", "Position and Space"],
        yearlyOutcomes: {
          "Beginning Year 1": [
            "Recognize and name common shapes in their environment",
            "Describe location of objects and familiar places"
          ],
          "Year 1": [
            "GI.1.V1: Identify and describe shapes in their environment",
            "GT.1.V1: Follow and give directions for moving from place to place using directional and positional language"
          ],
          "Year 2": [
            "GI.2.V1: Recognise, name, build, draw and sort 2 and 3-dimensional shapes",
            "GT.2.V1: Make and follow directions on simple maps"
          ],
          "Year 3": [
            "GI.3.V/E1: Compare and describe characteristics of common 2-dimensional and 3-dimensional shapes and objects",
            "GT.3.V/E1: Draw simple maps to represent familiar places"
          ],
          "Year 4": [
            "GPSS.4.1: Recognise, represent, compare and describe lines, angles, plane shapes and solids",
            "GO.4.1: Use simple maps and grids to represent position and follow directions"
          ],
          "Year 5": [
            "GPSS.5.1: Represent, compare and classify lines, angles, plane shapes and solids",
            "GO.5.1: Create, interpret and follow directions using simple maps"
          ],
          "Year 6": [
            "GPSS.6.1: Represent, classify and describe lines, angles, plane shapes and solids",
            "GO.6.1: Use a variety of mapping skills to create and interpret maps"
          ]
        }
      },
      {
        id: "patterns",
        title: "Patterns",
        color: "orange",
        subStrands: ["Patterns"],
        yearlyOutcomes: {
          "Beginning Year 1": [
            "Recognize and make patterns using local materials"
          ],
          "Year 1": [
            "PT.1.V1: Create, describe and extend regular and irregular patterns using local materials"
          ],
          "Year 2": [
            "PT.2.V1: Identify, create, describe, grow and shrink patterns using local and modern materials"
          ],
          "Year 3": [
            "PT.3.V/E1: Create, describe and complete a variety of patterns using numbers, shapes and traditional designs"
          ],
          "Year 4": [
            "PP.4.1: Create, describe, represent and extend whole number and simple geometric patterns"
          ],
          "Year 5": [
            "PP.5.1: Create, represent, explain and extend number and geometric patterns"
          ],
          "Year 6": [
            "PP.6.1: Create, represent, analyse and extend complex number and geometric patterns"
          ]
        }
      },
      {
        id: "chance-data",
        title: "Chance and Data",
        color: "red",
        subStrands: ["Probability", "Handling Data"],
        yearlyOutcomes: {
          "Beginning Year 1": [
            "Identify and describe events in the community",
            "Sort and talk about data about familiar topics"
          ],
          "Year 1": [
            "CDP.1.V1: Identify and describe events that may happen in the community",
            "CDHD.1.V1: Collect and organize simple data to represent information"
          ],
          "Year 2": [
            "CDP.2.V1: Identify and describe events that will happen, may happen and will never happen in the community",
            "CDHD.2.V1: Collect, organise, look at and discuss data"
          ],
          "Year 3": [
            "CDP.3.V/E1: Identify, sort and describe events in everyday life that involve chance",
            "CDHD.3.V/E1: Collect, organise, discuss and interpret data in a variety of ways"
          ],
          "Year 4": [
            "CDP.4.1: Predict, compare and sort the likelihood of events occurring in everyday life and in simple games",
            "CDD.4.1: Collect, organise, represent and interpret simple data"
          ],
          "Year 5": [
            "CDP.5.1: Predict, compare, order and explain the likely results of events and games involving chance",
            "CDD.5.1: Collect, organise, represent and interpret data using tables and graphs"
          ],
          "Year 6": [
            "CDP.6.1: Predict, record, compare and order the likely outcomes of events and games and represent as fractions or percentage",
            "CDD.6.1: Organise and represent data in a variety of ways and interpret the results"
          ]
        }
      }
    ]
  },

  // Science - Based on Vanuatu National Syllabus 2015 (Years 1-3)
  science: {
    subject: "Science",
    description: "Children investigate, understand and communicate about the natural world and the impact of humans on the natural world.",
    strands: [
      {
        id: "living-things",
        title: "Living Things and the Environment",
        color: "green",
        subStrands: ["Living Together", "Structure and Life Processes", "Biodiversity, Relationships and Sustainability"],
        yearlyOutcomes: {
          "Beginning Year 1": [
            "Talk about plants and animals found in their local environment"
          ],
          "Year 1": [
            "LLT.1.V1: Identify and record the different characteristics of common plants and animals",
            "LSL.1.V1: Explore the major external parts of green plants",
            "LB.1.V1: Be aware that living things live in various places and have different forms and ways of life"
          ],
          "Year 2": [
            "LLT.2.V1: Identify the basis of growth and development in plants and animals",
            "LSL.2.V1: Explore the major external parts of two and four-legged animals",
            "LB.2.V1: Explore the similarities and differences between living things"
          ],
          "Year 3": [
            "LLT.3.V/E1: Investigate how plants and animals in the local environment depend on each other",
            "LSL.3.V/E1: Identify the characteristics of vertebrates and flowering plants",
            "LB.3.V/E1: Describe and demonstrate different ways of sustaining the local resources (marine, land, air)"
          ],
          "Year 4": [
            "LE.LT.4.1: Recognise different habitats support different organisms",
            "LE.SL.4.1: Identify and describe the external body parts of animals and plants that help them to survive",
            "LE.SL.4.2: Identify human internal body parts that support and protect the body and help it move",
            "LE.BRS.4.1: Investigate the environment and recognise living things, populations and communities that live locally"
          ],
          "Year 5": [
            "LE.LT.5.1: Identify and describe plants and animals that live together in a local habitat",
            "LE.SL.5.1: Identify and describe the internal body parts of animals that support the digestive system",
            "LE.SL.5.2: Identify internal human body parts that help with digestion",
            "LE.BRS.5.1: Investigate and explain the effect on living things when the environment becomes unfavourable"
          ],
          "Year 6": [
            "LE.LT.6.1: Investigate and describe the energy pathway from the Sun to plants and animals",
            "LE.SL.6.1: Describe ways in which nutrients, water and oxygen are transported within plants",
            "LE.SL.6.2: Identify internal human body parts that help with breathing and circulation",
            "LE.BRS.6.1: Describe how physical features and other adaptations of living things help them to survive in their environment"
          ]
        }
      },
      {
        id: "matter",
        title: "Interactions of Matter",
        color: "blue",
        subStrands: ["Materials", "Forms and Cycles of Matter", "Reactions"],
        yearlyOutcomes: {
          "Beginning Year 1": [
            "Recognize that different materials are found all around us"
          ],
          "Year 1": [
            "IM.1.V1: Use the five senses to explore the characteristics of matter from their local environment"
          ],
          "Year 2": [
            "IM.2.V1: Explore how matter is used in different ways in everyday life",
            "IFC.2.V1: Investigate how raw materials in the environment are naturally recycled"
          ],
          "Year 3": [
            "IM.3.V/E1: Investigate how matter is conserved in their community",
            "IFC.3.V/E1: Explore how living things use natural materials to survive",
            "IR.3.V/E1: Investigate how common materials can be changed temporarily or permanently to form materials with new properties"
          ],
          "Year 4": [
            "IM.M.4.1: Recognise the physical world is made up of natural and synthetic materials with a variety of properties",
            "IM.FC.4.1: Recognise that water can exist in three interchangeable states of matter",
            "IM.R.4.1: Identify changes in the appearance and properties of common materials used locally"
          ],
          "Year 5": [
            "IM.M.5.1: Investigate the physical properties of natural and synthetic materials and demonstrate how these properties determine their uses",
            "IM.FC.5.1: Investigate and identify simple properties of solids, liquids and gases",
            "IM.R.5.1: Demonstrate and describe ways common materials can be changed temporarily or permanently to form materials with different properties"
          ],
          "Year 6": [
            "IM.M.6.1: Investigate, observe and record the changes that occur when combining materials",
            "IM.FC.6.1: Represent and explain the water cycle and show concern that water is a limited natural resource",
            "IM.R.6.1: Predict and conduct experiments to describe ways of producing permanent or temporary changes in some familiar materials"
          ]
        }
      },
      {
        id: "energy",
        title: "Energy and Everyday Life",
        color: "orange",
        subStrands: ["Energy Sources, Use and Conservation", "Energy Transfers and Transformation", "Forces"],
        yearlyOutcomes: {
          "Beginning Year 1": [
            "Investigate and identify energy as part of daily life",
            "Explore how objects are moved from one place to another"
          ],
          "Year 1": [
            "EES.1.V1: Investigate and identify energy as a part of daily life",
            "EF.1.V1: Explore how objects are moved from one place to another"
          ],
          "Year 2": [
            "EES.2.V1: Investigate and identify different forms of energy as part of daily life",
            "EF.2.V1: Recognise useful and destructive forces"
          ],
          "Year 3": [
            "EES.3.V/E1: Investigate different uses of energy as part of daily life",
            "EET.3.V/E1: Explore different ways in which energy is transferred and transformed",
            "EF.3.V/E1: Investigate and record situations where pushes, pulls and twists are used to move objects"
          ],
          "Year 4": [
            "EE.ET.4.1: Recognise that energy is required to make things work or move and that the Sun is our primary source of light and heat energy",
            "EE.ETC.4.1: Investigate and identify common sources, transfer and transformation of energy",
            "EE.F.4.1: Investigate and identify forces as a push or a pull"
          ],
          "Year 5": [
            "EE.ET.5.1: Recognise and give examples of various forms of energy",
            "EE.ETC.5.1: Investigate and explain ways energy is transformed in everyday life from one form to another",
            "EE.F.5.1: Demonstrate the effects of a force"
          ],
          "Year 6": [
            "EE.ET.6.1: Identify some common sources of heat and investigate materials which conduct heat",
            "EE.ETC.6.1: Describe ways to conserve energy use in our everyday lives and explain why it is important",
            "EE.F.6.1: Recognise, demonstrate and give examples of different types of forces in everyday life"
          ]
        }
      },
      {
        id: "earth-space",
        title: "Our Earth and Space",
        color: "purple",
        subStrands: ["Our Solar System", "Our Changing Earth"],
        yearlyOutcomes: {
          "Beginning Year 1": [
            "Show curiosity towards the day and night sky",
            "Observe how weather patterns affect living things"
          ],
          "Year 1": [
            "OEOS.1.V1: Show curiosity towards and recognise differences in the day and night sky",
            "OEOC.1.V1: Investigate and record observable natural events and changes that occur in the environment"
          ],
          "Year 2": [
            "OEOS.2.V1: Observe the major phases of the moon",
            "OEOC.2.V1: Recognize that weather can be predicted and daily activities can be planned accordingly"
          ],
          "Year 3": [
            "OEOS.3.V/E1: Identify constellations visible in the night sky over a period of time",
            "OEOC.3.V/E1: Identify local weather patterns and events and record using simple instruments"
          ],
          "Year 4": [
            "OES.OSS.4.1: Investigate and recognise that our solar system is made up of the Sun, the Earth and other planets",
            "OES.OCE.4.1: Investigate and describe features that occur naturally and those that are the result of human activities"
          ],
          "Year 5": [
            "OES.OSS.5.1: Investigate and explain the impacts of the Earth's rotation on its own axis once every day",
            "OES.OCE.5.1: Identify and explain causes and visible effects of natural processes and describe ways humans can minimise their impact"
          ],
          "Year 6": [
            "OES.OSS.6.1: Investigate and explain the impacts of the tilt of the Earth's axis and the orbiting of the Sun once every year",
            "OES.OCE.6.1: Identify and explain causes and visible effects of human activities and describe ways humans can minimise their impact"
          ]
        }
      },
      {
        id: "agriculture",
        title: "Agriculture",
        color: "teal",
        subStrands: ["Soil and Environment", "Growing Crops", "Farm Animals"],
        yearlyOutcomes: {
          "Year 4": [
            "A.SE.4.1: Identify the basic characteristics of a good garden soil and simple ways to increase soil fertility",
            "A.GC.4.1: Identify and apply basic skills to plant, feed, weed and harvest crops"
          ],
          "Year 5": [
            "A.SE.5.1: Demonstrate basic skills to prepare garden soil ready for planting crops",
            "A.GC.5.1: Identify the main tasks needed to grow and take care of common vegetables"
          ],
          "Year 6": [
            "A.SE.6.1: Apply basic skills and knowledge to make compost and control pests",
            "A.GC.6.1: Identify the main tasks needed to grow and take care of common garden fruits",
            "A.FA.6.1: Describe simple techniques to raise common farm animals"
          ]
        }
      }
    ]
  },

  // Living in our Community - Based on Vanuatu National Syllabus 2015 (Years 1-3)
  livingInCommunity: {
    subject: "Living in our Community",
    description: "Integrates healthy living, community relationships, cultural heritage, and spiritual development.",
    strands: [
      {
        id: "healthy-living",
        title: "Healthy Living",
        color: "red",
        subStrands: ["Healthy Individuals and Communities", "Movement and Games", "Safety"],
        yearlyOutcomes: {
          "Beginning Year 1": [
            "Follow simple rules of hygiene and explore how to stay healthy",
            "Use their body to move in different ways",
            "Recognize ways of acting safely at home and school"
          ],
          "Year 1": [
            "HLH.1.V1: Demonstrate healthy practices of hygiene, nutrition and self-care",
            "HLM.1.V1: Adapt their movements according to different environments and situations",
            "HLS.1.V1: Describe which people and things make environments and activities safe"
          ],
          "Year 2": [
            "HLH.2.V1: Demonstrate knowledge of healthy living practices",
            "HLM.2.V1: Demonstrate co-ordination and balance through participation in a wide variety of activities",
            "HLS.2.V1: Identify and describe ways to ensure their personal safety and the safety of others"
          ],
          "Year 3": [
            "HLH.3.V/E1: Demonstrate simple knowledge and basic skills to prevent illness and promote healthy living practices",
            "HLM.3.V/E1: Improve their physical abilities by participating in a diverse range of games and movement",
            "HLS.3.V/E1: Investigate situations or events that may be dangerous and describe ways to respond to these"
          ],
          "Year 4": [
            "HIC.PH.4.1: Identify and describe the food groups that promote good health",
            "HIC.PH.4.2: Describe and demonstrate personal hygiene practices to prevent illness",
            "HIC.GD.4.1: Recognise and describe simple ways to support healthy growth and development",
            "PE.MPA.4.1: Identify and demonstrate a range of locomotor and non-locomotor movements for coordination and fitness",
            "S.SS.4.1: Identify drugs in their local community that affect their health",
            "S.SS.4.2: Identify dangerous situations at home and school and describe ways to minimise risks"
          ],
          "Year 5": [
            "HIC.PH.5.1: Identify factors that influence eating habits and ways to overcome negative influences",
            "HIC.PH.5.2: Apply strategies to prevent common illness and disease",
            "HIC.GD.5.1: Identify the different stages of life and describe the changing health needs",
            "PE.MPA.5.1: Perform movement sequences in fitness and physical activities with coordination",
            "S.SS.5.1: Investigate and describe the effects of drugs on personal health",
            "S.SS.5.2: Investigate and identify dangerous situations in the community and describe ways to minimise risks"
          ],
          "Year 6": [
            "HIC.PH.6.1: Apply knowledge of good nutrition to demonstrate healthy food choices",
            "HIC.PH.6.2: Describe the causes of serious diseases and ways to prevent them",
            "HIC.GD.6.1: Identify changes that occur during puberty and ways to deal with the changes",
            "PE.MPA.6.1: Apply movement, flexibility, strength and balance by participating in a range of fitness and physical activities",
            "S.SS.6.1: Demonstrate strategies to make healthy choices about drugs",
            "S.SS.6.2: Describe actions and basic first aid techniques to use in common emergency situations"
          ]
        }
      },
      {
        id: "living-together",
        title: "Living Together",
        color: "blue",
        subStrands: ["Civics and Community Relationships", "Caring for the Environment", "Spiritual and Character Development"],
        yearlyOutcomes: {
          "Beginning Year 1": [
            "Recognize and talk about their roles in their family life",
            "Recognize who is in their immediate family",
            "Talk about some features of their local environment",
            "Explore the values and beliefs of the community"
          ],
          "Year 1": [
            "LTR.1.V1.A: Identify the roles and responsibilities they have in their family and community",
            "LTR.1.V1.B: Identify their relationships with family and community members",
            "LTC.1.V1: Take action to protect the local environment in which they live",
            "LTS.1.V1: Recognise some common religious and custom beliefs and values in their community"
          ],
          "Year 2": [
            "LTR.2.V1.A: Carry out responsibilities effectively on behalf of a class",
            "LTR.2.V1.B: Describe how some community members contribute to school and community life",
            "LTC.2.V1: Protect and develop the local environment in which they live",
            "LTS.2.V1: Identify and discuss important cultural and religious events in their community"
          ],
          "Year 3": [
            "LTR.3.V/E1.A: Display social responsible behaviour with peers",
            "LTR.3.V/E1.B: Demonstrate skills and attitudes which enable them to be a responsible member of the community",
            "LTC.3.V/E1: Develop skills to sustain the local environment in which they live",
            "LTS.3.V/E1: Discuss how values and beliefs influence people and the way they live"
          ],
          "Year 4": [
            "CHCV.4.1: Explore and identify the purposes of different cultural groups and the related values and beliefs",
            "CHSI.4.1: Identify and describe the islands of Vanuatu and their physical features",
            "CHSI.4.2: Understand and describe the early migration and history of Vanuatu",
            "CCLW.4.1: Investigate and describe how people in urban and rural communities meet their needs and wants",
            "CCCG.4.1: Understand the importance of rules and how decisions are made in the family, at school and in the community"
          ],
          "Year 5": [
            "CHCV.5.1: Demonstrate an understanding of different traditional beliefs, rituals, norms, practices and ways of communicating",
            "CHSI.5.1: Describe and compare the provinces, main towns and villages of Vanuatu",
            "CHSI.5.2: Describe the period of time when Europeans arrived in New Hebrides",
            "CCLW.5.1: Investigate and discuss the importance of different jobs and services in urban and rural communities",
            "CCCG.5.1: Identify and discuss rules, rights, freedoms, responsibilities, duties and decision making in the community"
          ],
          "Year 6": [
            "CHCV.6.1: Discuss and demonstrate the significant purposes of cultural ethics and practices, and how they are passed on and sustained",
            "CHSI.6.1: Describe and represent geographical features of Vanuatu and its place in the world",
            "CHSI.6.2: Investigate and describe the period of time during the Condominium and Independence",
            "CCLW.6.1: Investigate and discuss trading and the exchange of goods within Vanuatu",
            "CCCG.6.1: Investigate and identify how local systems of governance work"
          ]
        }
      },
      {
        id: "enterprise",
        title: "Enterprise",
        color: "purple",
        subStrands: ["Enterprise Values", "Enterprise Skills", "Enterprise Projects"],
        yearlyOutcomes: {
          "Year 4": [
            "EEV.4.1: Investigate and use basic enterprise values, skills and processes to solve simple practical problems"
          ],
          "Year 5": [
            "EEV.5.1: Investigate and apply enterprise values, skills and strategies to implement practical projects"
          ],
          "Year 6": [
            "EEV.6.1: Demonstrate enterprise values, skills and strategies to meet individual and community needs"
          ]
        }
      }
    ]
  },
  // Arts and Crafts - Based on Vanuatu National Syllabus 2013 (Years 4-6)
  artsAndCrafts: {
    subject: "Arts and Crafts",
    description: "Children develop creative skills through visual arts, performing arts (music, dance, drama), and traditional and contemporary crafts.",
    strands: [
      {
        id: "visual-arts",
        title: "Visual Arts",
        description: "Creating and appreciating visual art forms using various skills, techniques and processes",
        yearlyOutcomes: {
          "Year 4": [
            "VAC.4.1: Experiment with and use simple ideas, art skills, techniques and processes to create familiar visual art forms",
            "VAP.4.1: Present examples of artwork and describe the messages, ideas and feelings they are trying to convey"
          ],
          "Year 5": [
            "VAC.5.1: Investigate and apply arts skills, techniques and processes to create a range of visual art forms",
            "VAP.5.1: Present examples of artwork and explain the significance of the designs and techniques used to create them"
          ],
          "Year 6": [
            "VAC.6.1: Apply arts skills, techniques and processes to design and create a range of visual art forms that convey their own ideas and feelings",
            "VAP.6.1: Present examples of artwork and discuss how different techniques are used to convey different messages, ideas and feelings"
          ]
        }
      },
      {
        id: "performing-arts",
        title: "Performing Arts",
        description: "Creating and performing music, dance and drama to express ideas and feelings",
        yearlyOutcomes: {
          "Year 4": [
            "PAC.4.1: Experiment with and use knowledge, skills and techniques to create simple forms of music, dance and drama",
            "PAP.4.1: Perform simple forms of music, dance and drama and describe the messages, ideas and feelings they are trying to convey"
          ],
          "Year 5": [
            "PAC.5.1: Investigate and apply knowledge, skills and techniques to create a range of music, dance and drama",
            "PAP.5.1: Perform music, dance and drama and explain the significance of the music and movements and the techniques used to create them"
          ],
          "Year 6": [
            "PAC.6.1: Apply knowledge, skills and techniques to plan and create music, dance and drama that convey their own ideas and feelings",
            "PAP.6.1: Demonstrate skills and techniques in their own performances and discuss how messages, ideas and feelings are conveyed"
          ]
        }
      },
      {
        id: "crafts",
        title: "Crafts",
        description: "Designing and creating functional and artistic crafts using traditional and contemporary techniques",
        yearlyOutcomes: {
          "Year 4": [
            "CC.4.1: Experiment with and use simple ideas, skills, techniques and processes to create familiar crafts",
            "CPE.4.1: Present examples of craftwork and describe their purpose and the significance of the designs"
          ],
          "Year 5": [
            "CC.5.1: Investigate and apply knowledge, skills, techniques and processes to create a range of crafts",
            "CPE.5.1: Present examples of craftwork and explain the significance of the designs and techniques used to create them"
          ],
          "Year 6": [
            "CC.6.1: Apply knowledge, skills, techniques and processes to design and create a range of crafts that are artistic and functional",
            "CPE.6.1: Present examples of craftwork and discuss their functions and how the techniques and designs are used to convey different messages, ideas and feelings"
          ]
        }
      }
    ]
  },
  // Health and Physical Education - Based on Vanuatu National Syllabus 2013 (Years 4-6)
  healthPE: {
    subject: "Health and Physical Education",
    description: "Children develop knowledge, skills and attitudes to maintain personal health, physical fitness and safety in their communities.",
    strands: [
      {
        id: "personal-health",
        title: "Personal Health",
        description: "Understanding nutrition, hygiene, growth and development, and community health",
        yearlyOutcomes: {
          "Year 4": [
            "HIC.PH.4.1: Identify and describe the food groups that promote good health",
            "HIC.PH.4.2: Describe and demonstrate personal hygiene practices to prevent illness",
            "HIC.GD.4.1: Recognise and describe simple ways to support healthy growth and development",
            "HIC.HC.4.1: Recognise and describe features of a healthy living environment"
          ],
          "Year 5": [
            "HIC.PH.5.1: Identify factors that influence eating habits and ways to overcome negative influences",
            "HIC.PH.5.2: Apply strategies to prevent common illness and disease",
            "HIC.GD.5.1: Identify the different stages of life and describe the changing health needs",
            "HIC.HC.5.1: Identify and describe the role of health services in the community"
          ],
          "Year 6": [
            "HIC.PH.6.1: Apply knowledge of good nutrition to demonstrate healthy food choices",
            "HIC.PH.6.2: Describe the causes of serious diseases and ways to prevent them",
            "HIC.GD.6.1: Identify changes that occur during puberty and ways to deal with the changes",
            "HIC.HC.6.1: Apply knowledge to promote healthy living in their communities"
          ]
        }
      },
      {
        id: "physical-education",
        title: "Physical Education",
        description: "Developing movement skills, coordination and fitness through physical activities and sports",
        yearlyOutcomes: {
          "Year 4": [
            "PE.MPA.4.1: Identify and demonstrate a range of locomotor and non-locomotor movements for coordination and fitness",
            "PE.MPA.4.2: Identify and demonstrate basic skills in a range of games"
          ],
          "Year 5": [
            "PE.MPA.5.1: Perform movement sequences in fitness and physical activities with coordination",
            "PE.MPA.5.2: Perform basic skills in a range of games and modified sports"
          ],
          "Year 6": [
            "PE.MPA.6.1: Apply movement, flexibility, strength and balance by participating in a range of fitness and physical activities",
            "PE.MPA.6.2: Apply skills and techniques by participating in a range of modified sports"
          ]
        }
      },
      {
        id: "safety",
        title: "Safety",
        description: "Understanding drug awareness and safety in different situations",
        yearlyOutcomes: {
          "Year 4": [
            "S.SS.4.1: Identify drugs in their local community that affect their health",
            "S.SS.4.2: Identify dangerous situations at home and school and describe ways to minimise risks"
          ],
          "Year 5": [
            "S.SS.5.1: Investigate and describe the effects of drugs on personal health",
            "S.SS.5.2: Investigate and identify dangerous situations in the community and describe ways to minimise risks"
          ],
          "Year 6": [
            "S.SS.6.1: Demonstrate strategies to make healthy choices about drugs",
            "S.SS.6.2: Describe actions and basic first aid techniques to use in common emergency situations"
          ]
        }
      }
    ]
  }
};

// Merged Sample Activities (Years 1-6)
export const sampleActivities = {
  "Year 1": {
    languageCommunication: [
      { activity: "Letter Sound Hunt", description: "Find objects in the classroom that start with the letter of the day", strand: "Reading" },
      { activity: "Story Circle", description: "Students take turns adding sentences to create a class story", strand: "Listening and Speaking" },
      { activity: "Picture Dictionary", description: "Create a class picture dictionary with vernacular words", strand: "Writing" },
      { activity: "Sign Walk", description: "Walk around school identifying signs and symbols", strand: "Viewing" }
    ],
    mathematics: [
      { activity: "Counting Shells", description: "Use local shells to practice counting to 19", strand: "Number" },
      { activity: "Body Measuring", description: "Measure classroom objects using hand spans", strand: "Measurement" },
      { activity: "Shape Hunt", description: "Find circles, squares, and triangles around the school", strand: "Geometry" },
      { activity: "Pattern Necklace", description: "Create patterns using seeds and shells", strand: "Patterns" }
    ],
    science: [
      { activity: "Plant Parts", description: "Label parts of local plants (roots, stem, leaves, flower)", strand: "Living Things and the Environment" },
      { activity: "Senses Walk", description: "Explore the environment using all five senses to identify characteristics of matter", strand: "Interactions of Matter" },
      { activity: "Sun Position", description: "Track where the sun is at different times of day", strand: "Our Earth and Space" },
      { activity: "Push and Pull", description: "Sort objects by how they are moved", strand: "Energy and Everyday Life" }
    ],
    livingInCommunity: [
      { activity: "Hygiene Song", description: "Learn and sing a song about washing hands", strand: "Healthy Living" },
      { activity: "Family Tree", description: "Draw your family tree showing all members", strand: "Living Together" },
      { activity: "Traditional Dance", description: "Learn a simple traditional dance from your island", strand: "Ways of Living" }
    ]
  },
  "Year 2": {
    languageCommunication: [
      { activity: "Reading Buddies", description: "Pair up to read stories together", strand: "Reading" },
      { activity: "Interview a Community Member", description: "Practice asking and answering questions", strand: "Listening and Speaking" },
      { activity: "Class Newsletter", description: "Write simple news items for a class newsletter", strand: "Writing" },
      { activity: "Create a Poster", description: "Design posters with pictures and symbols", strand: "Viewing" }
    ],
    mathematics: [
      { activity: "Market Math", description: "Use play money to buy and sell items", strand: "Number" },
      { activity: "Calendar Activities", description: "Track events and weather on a class calendar", strand: "Measurement" },
      { activity: "Map Making", description: "Draw a simple map of the classroom", strand: "Geometry" },
      { activity: "Growing Patterns", description: "Create patterns that grow or shrink", strand: "Patterns" }
    ],
    science: [
      { activity: "Life Cycle Observation", description: "Observe and draw the life cycle of a butterfly or frog", strand: "Living Things and the Environment" },
      { activity: "Recycling Sort", description: "Sort materials that decay vs those that don't - investigate natural recycling", strand: "Interactions of Matter" },
      { activity: "Weather Diary", description: "Keep a daily record of weather observations and predict activities accordingly", strand: "Our Earth and Space" },
      { activity: "Energy Sources", description: "Identify different forms of energy used in the community", strand: "Energy and Everyday Life" }
    ],
    livingInCommunity: [
      { activity: "Food Groups Collage", description: "Create a collage showing three food groups", strand: "Healthy Living" },
      { activity: "Community Helpers", description: "Interview community helpers about their roles", strand: "Living Together" },
      { activity: "Traditional Craft", description: "Make a simple traditional craft with guidance", strand: "Ways of Living" }
    ]
  },
  "Year 3": {
    languageCommunication: [
      { activity: "English Word Wall", description: "Build a classroom word wall with new English words", strand: "Reading" },
      { activity: "Show and Tell in English", description: "Present an item to the class using simple English", strand: "Listening and Speaking" },
      { activity: "Story Writing", description: "Write and illustrate a short story in English", strand: "Writing" },
      { activity: "Media Analysis", description: "Discuss how pictures and words work together in books", strand: "Viewing" }
    ],
    mathematics: [
      { activity: "Place Value Games", description: "Use bundles of sticks to understand hundreds, tens, ones", strand: "Number" },
      { activity: "Clock Making", description: "Make and use paper clocks to tell time", strand: "Measurement" },
      { activity: "Symmetry Art", description: "Create symmetrical designs and identify lines of symmetry", strand: "Geometry" },
      { activity: "Number Patterns", description: "Find and extend number patterns on a number line", strand: "Patterns" }
    ],
    science: [
      { activity: "Food Chain Model", description: "Create a model showing how local plants and animals depend on each other", strand: "Living Things and the Environment" },
      { activity: "Material Changes", description: "Investigate how common materials can be changed temporarily or permanently", strand: "Interactions of Matter" },
      { activity: "Weather Station", description: "Use simple instruments to measure and record local weather patterns", strand: "Our Earth and Space" },
      { activity: "Force Investigation", description: "Investigate and record situations where pushes, pulls and twists are used to move objects", strand: "Energy and Everyday Life" }
    ],
    livingInCommunity: [
      { activity: "First Aid Basics", description: "Learn basic first aid steps", strand: "Healthy Living" },
      { activity: "Environmental Project", description: "Plan and carry out a class clean-up project", strand: "Living Together" },
      { activity: "Cultural Presentation", description: "Research and present about a different island's customs", strand: "Ways of Living" }
    ]
  },
  ...sampleActivitiesYears4to6
};

// Assessment samples
export const assessmentSamples = {
  "Year 1": {
    languageCommunication: [
      {
        id: "y1-lang-1",
        title: "Letter Sounds Quiz",
        type: "quiz",
        questions: [
          { question: "What sound does the letter 'M' make?", options: ["mmm", "sss", "ttt"], answer: "mmm" },
          { question: "Which word starts with 'B'?", options: ["Cat", "Ball", "Dog"], answer: "Ball" },
          { question: "Find the word that rhymes with 'cat'", options: ["Dog", "Hat", "Sun"], answer: "Hat" },
          { question: "How many syllables in 'banana'?", options: ["2", "3", "4"], answer: "3" },
          { question: "Which letter comes after 'C'?", options: ["B", "D", "E"], answer: "D" }
        ]
      },
      {
        id: "y1-lang-2",
        title: "Listening Comprehension",
        type: "activity",
        description: "Listen to the teacher read a short story and answer questions about it.",
        skills: ["Following instructions", "Recalling main ideas", "Identifying characters"]
      }
    ],
    mathematics: [
      {
        id: "y1-math-1",
        title: "Counting and Numbers Quiz",
        type: "quiz",
        questions: [
          { question: "Count the shells: 🐚🐚🐚🐚🐚", options: ["4", "5", "6"], answer: "5" },
          { question: "What number comes after 12?", options: ["11", "13", "14"], answer: "13" },
          { question: "5 + 3 = ?", options: ["7", "8", "9"], answer: "8" },
          { question: "Which is bigger: 9 or 7?", options: ["9", "7", "Same"], answer: "9" },
          { question: "10 - 4 = ?", options: ["5", "6", "7"], answer: "6" }
        ]
      },
      {
        id: "y1-math-2",
        title: "Shapes Recognition",
        type: "practical",
        description: "Identify circles, squares, and triangles in the classroom.",
        skills: ["Shape recognition", "Counting sides", "Finding shapes in environment"]
      }
    ]
  },
  "Year 2": {
    languageCommunication: [
      {
        id: "y2-lang-1",
        title: "Reading Comprehension",
        type: "quiz",
        questions: [
          { question: "In the story, what did the boy find?", options: ["A shell", "A fish", "A bird"], answer: "A shell" },
          { question: "Where did the story take place?", options: ["Mountain", "Beach", "Village"], answer: "Beach" },
          { question: "How did the character feel at the end?", options: ["Sad", "Happy", "Angry"], answer: "Happy" },
          { question: "What is the main idea of the story?", options: ["Sharing is good", "Be careful", "Work hard"], answer: "Sharing is good" }
        ]
      }
    ],
    mathematics: [
      {
        id: "y2-math-1",
        title: "Two-Digit Numbers",
        type: "quiz",
        questions: [
          { question: "What is 25 + 14?", options: ["38", "39", "40"], answer: "39" },
          { question: "Which number is odd: 24, 37, 42?", options: ["24", "37", "42"], answer: "37" },
          { question: "50 - 23 = ?", options: ["27", "28", "37"], answer: "27" },
          { question: "Put in order: 45, 32, 67", options: ["67, 45, 32", "32, 45, 67", "45, 32, 67"], answer: "32, 45, 67" },
          { question: "What is half of 20?", options: ["5", "10", "15"], answer: "10" }
        ]
      }
    ]
  },
  "Year 3": {
    languageCommunication: [
      {
        id: "y3-lang-1",
        title: "English Vocabulary",
        type: "quiz",
        questions: [
          { question: "What is the English word for 'wota' (water)?", options: ["Water", "Watter", "Woter"], answer: "Water" },
          { question: "Choose the correct sentence:", options: ["He go school", "He goes to school", "He going school"], answer: "He goes to school" },
          { question: "What is the opposite of 'big'?", options: ["Large", "Small", "Tall"], answer: "Small" },
          { question: "Complete: The sun ____ in the east.", options: ["rise", "rises", "rising"], answer: "rises" }
        ]
      }
    ],
    mathematics: [
      {
        id: "y3-math-1",
        title: "Three-Digit Numbers and Operations",
        type: "quiz",
        questions: [
          { question: "What is 245 + 123?", options: ["358", "368", "378"], answer: "368" },
          { question: "In 572, what digit is in the tens place?", options: ["5", "7", "2"], answer: "7" },
          { question: "500 - 234 = ?", options: ["266", "276", "366"], answer: "266" },
          { question: "3 x 4 = ?", options: ["7", "12", "14"], answer: "12" },
          { question: "Round 478 to the nearest hundred", options: ["400", "500", "480"], answer: "500" }
        ]
      }
    ],
    science: [
      {
        id: "y3-sci-1",
        title: "Living Things Quiz",
        type: "quiz",
        questions: [
          { question: "Which animal is a vertebrate?", options: ["Worm", "Fish", "Jellyfish"], answer: "Fish" },
          { question: "What do plants need to grow?", options: ["Water, sunlight, soil", "Only water", "Only sunlight"], answer: "Water, sunlight, soil" },
          { question: "What is a food chain?", options: ["A list of foods", "How energy passes from one living thing to another", "A type of plant"], answer: "How energy passes from one living thing to another" },
          { question: "Which is a flowering plant?", options: ["Fern", "Hibiscus", "Moss"], answer: "Hibiscus" }
        ]
      }
    ]
  },
  ...assessmentSamplesYears4to6
};

// Yearly Planner Templates
export const yearlyPlannerTemplates = {
  "Year 1": {
    term1: {
      weeks: "1-10",
      themes: ["Getting to Know Each Other", "My Family", "My School"],
      subjects: {
        languageCommunication: ["Letter recognition A-M", "Listening skills", "Pre-writing", "Basic signs"],
        mathematics: ["Counting 1-10", "Basic shapes", "Non-standard measurement", "Simple patterns"],
        science: ["Living vs non-living", "My body parts", "Weather observation"],
        livingInCommunity: ["Hygiene rules", "Family roles", "Basic movement"]
      }
    },
    term2: {
      weeks: "1-10",
      themes: ["Our Environment", "Plants Around Us", "Animals Around Us"],
      subjects: {
        languageCommunication: ["Letter recognition N-Z", "Story listening", "Name writing", "Symbols in community"],
        mathematics: ["Counting 11-19", "Addition up to 10", "Position words", "Pattern creation"],
        science: ["Plant parts", "Animal characteristics", "Sun and moon"],
        livingInCommunity: ["Food groups", "Keeping safe", "Traditional songs"]
      }
    },
    term3: {
      weeks: "1-10",
      themes: ["Our Community", "Celebrations", "Working Together"],
      subjects: {
        languageCommunication: ["Reading simple words", "Retelling stories", "Writing sentences", "Community signs"],
        mathematics: ["Subtraction up to 10", "Time markers", "Money recognition", "Extending patterns"],
        science: ["Matter around us", "Energy sources", "Seasons"],
        livingInCommunity: ["Community helpers", "Customs", "Traditional dance"]
      }
    },
    term4: {
      weeks: "1-10",
      themes: ["Review and Consolidation", "End of Year Celebration", "Looking Ahead"],
      subjects: {
        languageCommunication: ["Reading consolidation", "Story writing", "Oral presentations", "Review activities"],
        mathematics: ["Number review 1-20", "Operations review", "Measurement review", "Pattern review"],
        science: ["Science project", "Review topics", "Nature exploration"],
        livingInCommunity: ["Year achievements", "Cultural celebration", "Graduation preparation"]
      }
    }
  },
  "Year 2": {
    term1: {
      weeks: "1-10",
      themes: ["New Beginnings", "My Village", "Healthy Me"],
      subjects: {
        languageCommunication: ["Reading fluency", "Speaking in groups", "Writing simple texts", "Visual messages"],
        mathematics: ["Numbers to 50", "Addition two-digit", "Standard measurement intro", "Growing patterns"],
        science: ["Life cycles", "Materials in community", "Weather prediction"],
        livingInCommunity: ["Body systems", "Rules and responsibilities", "Traditional crafts"]
      }
    },
    term2: {
      weeks: "1-10",
      themes: ["Our Island", "Water and Land", "Living Things"],
      subjects: {
        languageCommunication: ["Reading comprehension", "Formal speaking", "Narrative writing", "Poster creation"],
        mathematics: ["Numbers to 99", "Subtraction two-digit", "Maps and directions", "Shrinking patterns"],
        science: ["Animal classification", "Recycling", "Forces in nature"],
        livingInCommunity: ["Sports and games", "Environment care", "Cultural events"]
      }
    },
    term3: {
      weeks: "1-10",
      themes: ["Vanuatu Culture", "Preparing for Year 3", "Celebration"],
      subjects: {
        languageCommunication: ["Independent reading", "Presentations", "Story writing", "Interpreting visuals"],
        mathematics: ["Fractions intro", "Time on clock", "Money problems", "Data collection"],
        science: ["Conservation", "Energy use", "Seasons and farming"],
        livingInCommunity: ["Character development", "Community projects", "Performance arts"]
      }
    },
    term4: {
      weeks: "1-10",
      themes: ["Review and Assessment", "End of Year Projects", "Year 3 Preparation"],
      subjects: {
        languageCommunication: ["Reading review", "Speaking assessment", "Writing portfolio", "Visual project"],
        mathematics: ["Number review to 99", "Operations mastery", "Measurement review", "Problem solving"],
        science: ["Science fair", "Environment project", "Weather journal"],
        livingInCommunity: ["Health review", "Community service", "Cultural showcase"]
      }
    }
  },
  "Year 3": {
    term1: {
      weeks: "1-10",
      themes: ["Introduction to English", "Our Pacific Region", "Numbers Everywhere"],
      subjects: {
        languageCommunication: ["English vocabulary", "English listening", "Print to cursive", "Visual analysis"],
        mathematics: ["Numbers to 500", "Addition 3-digit", "Standard units", "Number patterns"],
        science: ["Vertebrates", "Matter changes", "Weather instruments"],
        livingInCommunity: ["First aid", "Community roles", "Cultural heritage"]
      }
    },
    term2: {
      weeks: "1-10",
      themes: ["English Reading", "Science Discovery", "Problem Solving"],
      subjects: {
        languageCommunication: ["Simple English texts", "Speaking in English", "Writing in English", "Creating visuals"],
        mathematics: ["Numbers to 999", "Multiplication intro", "Fractions", "Chance events"],
        science: ["Food chains", "Natural materials", "Constellations"],
        livingInCommunity: ["Team sports", "Sustainability", "Arts and crafts"]
      }
    },
    term3: {
      weeks: "1-10",
      themes: ["Preparation for Year 4", "Island Studies", "Celebration of Learning"],
      subjects: {
        languageCommunication: ["Reading comprehension", "English conversations", "Story completion", "Media literacy"],
        mathematics: ["All operations", "Time and money", "Geometry review", "Data interpretation"],
        science: ["Energy transfer", "Forces", "Climate"],
        livingInCommunity: ["Safety skills", "Cultural presentations", "Musical performance"]
      }
    },
    term4: {
      weeks: "1-10",
      themes: ["Final Assessment", "Year 4 Preparation", "Graduation Celebration"],
      subjects: {
        languageCommunication: ["English reading assessment", "Speaking portfolio", "Writing assessment", "Media project"],
        mathematics: ["Number mastery to 999", "All operations review", "Geometry assessment", "Data project"],
        science: ["Science portfolio", "Investigation skills", "Year review"],
        livingInCommunity: ["Health assessment", "Cultural project", "Performance showcase"]
      }
    }
  },
  ...yearlyPlannerTemplatesYears4to6
};

// Scheme of Work Templates
export const schemeOfWorkTemplates = {
  "Year 1": {
    languageCommunication: {
      term1: [
        { week: 1, topic: "Introduction to School", outcomes: "Follow classroom instructions", activities: "Name games, listening activities" },
        { week: 2, topic: "Letter Aa", outcomes: "Recognize and write letter A", activities: "Letter tracing, sound identification" },
        { week: 3, topic: "Letter Bb", outcomes: "Recognize and write letter B", activities: "Letter tracing, picture matching" },
        { week: 4, topic: "Letter Cc", outcomes: "Recognize and write letter C", activities: "Letter formation, word hunt" },
        { week: 5, topic: "Review A-C", outcomes: "Identify letters A, B, C", activities: "Letter games, simple reading" },
        { week: 6, topic: "Letter Dd", outcomes: "Recognize and write letter D", activities: "Drawing, letter tracing" },
        { week: 7, topic: "Letter Ee", outcomes: "Recognize and write letter E", activities: "Environmental print" },
        { week: 8, topic: "Letters Ff-Gg", outcomes: "Recognize letters F and G", activities: "Alphabet songs, letter sorting" },
        { week: 9, topic: "Letters Hh-Ii", outcomes: "Recognize letters H and I", activities: "Letter hunts, phonics games" },
        { week: 10, topic: "Review A-I", outcomes: "Read simple words", activities: "Word building, reading practice" }
      ]
    },
    mathematics: {
      term1: [
        { week: 1, topic: "Counting 1-5", outcomes: "Count objects to 5", activities: "Counting songs, object sorting" },
        { week: 2, topic: "Counting 6-10", outcomes: "Count objects to 10", activities: "Number rhymes, counting games" },
        { week: 3, topic: "Writing Numbers 1-5", outcomes: "Write numerals 1-5", activities: "Number tracing, sand writing" },
        { week: 4, topic: "Writing Numbers 6-10", outcomes: "Write numerals 6-10", activities: "Number formation practice" },
        { week: 5, topic: "Comparing Numbers", outcomes: "Compare groups of objects", activities: "More/less activities" },
        { week: 6, topic: "Circle Shape", outcomes: "Identify circles", activities: "Shape hunt, circle art" },
        { week: 7, topic: "Square Shape", outcomes: "Identify squares", activities: "Building with shapes" },
        { week: 8, topic: "Triangle Shape", outcomes: "Identify triangles", activities: "Pattern making with shapes" },
        { week: 9, topic: "Patterns", outcomes: "Recognize simple patterns", activities: "Pattern extension activities" },
        { week: 10, topic: "Review", outcomes: "Apply number and shape skills", activities: "Math games, assessment" }
      ]
    },
    science: {
      term1: [
        { week: 1, topic: "My Body Parts", outcomes: "Name basic body parts", activities: "Body songs, pointing games" },
        { week: 2, topic: "My Five Senses", outcomes: "Identify and use five senses", activities: "Sensory stations, guessing games" },
        { week: 3, topic: "Taking Care of My Body", outcomes: "Practice good hygiene habits", activities: "Handwashing practice, hygiene posters" },
        { week: 4, topic: "Living and Non-Living", outcomes: "Sort living and non-living things", activities: "Nature walk, sorting activities" },
        { week: 5, topic: "Plants Around Us", outcomes: "Identify common plants", activities: "Plant walk, leaf collection" },
        { week: 6, topic: "Animals Around Us", outcomes: "Name local animals", activities: "Animal sounds, picture matching" },
        { week: 7, topic: "Weather Today", outcomes: "Describe daily weather", activities: "Weather chart, dressing appropriately" },
        { week: 8, topic: "Day and Night", outcomes: "Understand day/night cycle", activities: "Shadow play, sun observation" },
        { week: 9, topic: "Clean Water", outcomes: "Know importance of clean water", activities: "Water sources, water safety" },
        { week: 10, topic: "Review", outcomes: "Demonstrate science learning", activities: "Science games, simple projects" }
      ]
    },
    livingInCommunity: {
      term1: [
        { week: 1, topic: "My Family", outcomes: "Name family members and their roles", activities: "Family tree drawing, sharing" },
        { week: 2, topic: "My Home", outcomes: "Describe where we live", activities: "Home drawing, room names" },
        { week: 3, topic: "Being Healthy and Clean", outcomes: "Practice daily hygiene routines", activities: "Handwashing steps, tooth brushing" },
        { week: 4, topic: "Classroom Rules", outcomes: "Follow classroom rules", activities: "Rule creation, role play" },
        { week: 5, topic: "Being a Good Friend", outcomes: "Show kindness to others", activities: "Sharing activities, friendship stories" },
        { week: 6, topic: "Helpers in Our Community", outcomes: "Identify community helpers", activities: "Helper pictures, role play" },
        { week: 7, topic: "Keeping Safe at Home", outcomes: "Know basic safety rules", activities: "Safety scenarios, danger signs" },
        { week: 8, topic: "Keeping Safe Outside", outcomes: "Practice road and water safety", activities: "Safety songs, rules practice" },
        { week: 9, topic: "Taking Care of Our Things", outcomes: "Show responsibility for belongings", activities: "Tidying up, caring for books" },
        { week: 10, topic: "Review", outcomes: "Demonstrate community learning", activities: "Role play, assessment" }
      ]
    }
  },
  "Year 2": {
    languageCommunication: {
      term1: [
        { week: 1, topic: "Reading Fluency", outcomes: "Read simple sentences fluently", activities: "Echo reading, partner reading" },
        { week: 2, topic: "Story Elements", outcomes: "Identify characters and setting", activities: "Story mapping, character description" },
        { week: 3, topic: "Writing Simple Sentences", outcomes: "Write complete sentences", activities: "Sentence building, dictation" },
        { week: 4, topic: "Question Words", outcomes: "Use who, what, where, when, why", activities: "Question games, interviews" },
        { week: 5, topic: "Retelling Stories", outcomes: "Retell stories in sequence", activities: "Sequencing cards, story retelling" },
        { week: 6, topic: "Rhyming Words", outcomes: "Identify and create rhymes", activities: "Rhyming games, poetry" },
        { week: 7, topic: "Describing Words", outcomes: "Use adjectives in writing", activities: "Describing objects, expanding sentences" },
        { week: 8, topic: "Instructions", outcomes: "Follow and give simple instructions", activities: "Giving directions, making things" },
        { week: 9, topic: "Personal Narratives", outcomes: "Write about personal experiences", activities: "Journal writing, sharing stories" },
        { week: 10, topic: "Review", outcomes: "Demonstrate language skills", activities: "Reading, writing assessment" }
      ]
    },
    mathematics: {
      term1: [
        { week: 1, topic: "Numbers to 100", outcomes: "Count, read, write to 100", activities: "Hundred chart, counting by 10s" },
        { week: 2, topic: "Place Value", outcomes: "Understand tens and ones", activities: "Base-10 blocks, place value games" },
        { week: 3, topic: "Addition to 20", outcomes: "Add numbers to 20", activities: "Number bonds, addition stories" },
        { week: 4, topic: "Subtraction to 20", outcomes: "Subtract numbers to 20", activities: "Take away games, fact families" },
        { week: 5, topic: "2D Shapes", outcomes: "Name and describe 2D shapes", activities: "Shape sorting, shape pictures" },
        { week: 6, topic: "3D Shapes", outcomes: "Identify basic 3D shapes", activities: "Shape hunt, building shapes" },
        { week: 7, topic: "Measurement Length", outcomes: "Compare and measure lengths", activities: "Measuring with units, comparison" },
        { week: 8, topic: "Time - Hours", outcomes: "Tell time to the hour", activities: "Clock activities, daily schedule" },
        { week: 9, topic: "Money Recognition", outcomes: "Recognize Vanuatu coins", activities: "Coin sorting, shop play" },
        { week: 10, topic: "Review", outcomes: "Demonstrate math skills", activities: "Math games, assessment" }
      ]
    },
    science: {
      term1: [
        { week: 1, topic: "Plant Parts", outcomes: "Name and describe plant parts", activities: "Plant labeling, real plant observation" },
        { week: 2, topic: "What Plants Need", outcomes: "Identify plant needs", activities: "Planting seeds, growth observation" },
        { week: 3, topic: "Life Cycle of Plants", outcomes: "Describe seed to plant cycle", activities: "Growth diary, sequencing" },
        { week: 4, topic: "Animal Homes", outcomes: "Match animals to their habitats", activities: "Habitat dioramas, sorting games" },
        { week: 5, topic: "Animal Babies", outcomes: "Name animal babies", activities: "Matching games, life cycles" },
        { week: 6, topic: "Push and Pull", outcomes: "Identify push and pull forces", activities: "Force experiments, toy exploration" },
        { week: 7, topic: "Materials Around Us", outcomes: "Classify materials by properties", activities: "Material hunt, texture sorting" },
        { week: 8, topic: "Water in Our World", outcomes: "Describe uses of water", activities: "Water cycle introduction, conservation" },
        { week: 9, topic: "Caring for Environment", outcomes: "Explain ways to protect environment", activities: "Reduce, reuse, recycle activities" },
        { week: 10, topic: "Review", outcomes: "Demonstrate science understanding", activities: "Science projects, assessment" }
      ]
    },
    livingInCommunity: {
      term1: [
        { week: 1, topic: "Rules at Home and School", outcomes: "Explain importance of rules", activities: "Rule making, consequence discussion" },
        { week: 2, topic: "Rights and Responsibilities", outcomes: "Identify basic rights", activities: "Rights posters, responsibility matching" },
        { week: 3, topic: "Healthy Foods", outcomes: "Identify healthy food choices", activities: "Food sorting, meal planning" },
        { week: 4, topic: "Exercise and Rest", outcomes: "Explain importance of exercise", activities: "Movement activities, rest discussion" },
        { week: 5, topic: "Keeping Safe", outcomes: "Know safety rules", activities: "Safety scenarios, emergency practice" },
        { week: 6, topic: "Our Environment", outcomes: "Describe our local environment", activities: "Environment walk, observation" },
        { week: 7, topic: "Caring for Our Environment", outcomes: "Show ways to care for environment", activities: "Clean up activity, planting" },
        { week: 8, topic: "Working Together", outcomes: "Cooperate in group activities", activities: "Team games, group projects" },
        { week: 9, topic: "Resolving Conflicts", outcomes: "Use words to solve problems", activities: "Role play, peace making" },
        { week: 10, topic: "Review", outcomes: "Demonstrate community knowledge", activities: "Projects, assessment" }
      ]
    }
  },
  "Year 3": {
    languageCommunication: {
      term1: [
        { week: 1, topic: "Introduction to English", outcomes: "Use basic English greetings", activities: "Greeting practice, vocabulary introduction" },
        { week: 2, topic: "Listening Skills", outcomes: "Follow multi-step instructions", activities: "Simon Says, listening tasks" },
        { week: 3, topic: "Reading Comprehension", outcomes: "Answer questions about texts", activities: "Guided reading, comprehension questions" },
        { week: 4, topic: "Paragraph Writing", outcomes: "Write simple paragraphs", activities: "Paragraph structure, topic sentences" },
        { week: 5, topic: "Vocabulary Building", outcomes: "Learn and use new vocabulary", activities: "Word walls, vocabulary games" },
        { week: 6, topic: "Grammar: Nouns and Verbs", outcomes: "Identify nouns and verbs", activities: "Sorting games, sentence building" },
        { week: 7, topic: "Story Writing", outcomes: "Write short stories with beginning, middle, end", activities: "Story planning, creative writing" },
        { week: 8, topic: "Oral Presentation", outcomes: "Present ideas to small groups", activities: "Show and tell, mini presentations" },
        { week: 9, topic: "Poetry Introduction", outcomes: "Read and respond to poems", activities: "Poetry reading, simple poem writing" },
        { week: 10, topic: "Review", outcomes: "Demonstrate language progress", activities: "Portfolio review, assessment" }
      ]
    },
    mathematics: {
      term1: [
        { week: 1, topic: "Numbers to 500", outcomes: "Read, write, compare to 500", activities: "Number building, comparison" },
        { week: 2, topic: "Place Value (Hundreds)", outcomes: "Understand place value to hundreds", activities: "Base-10 work, expanded form" },
        { week: 3, topic: "Addition with Regrouping", outcomes: "Add with carrying", activities: "Column addition, word problems" },
        { week: 4, topic: "Subtraction with Regrouping", outcomes: "Subtract with borrowing", activities: "Column subtraction, problem solving" },
        { week: 5, topic: "Introduction to Multiplication", outcomes: "Understand multiplication as groups", activities: "Arrays, repeated addition" },
        { week: 6, topic: "Times Tables (2, 5, 10)", outcomes: "Recall basic multiplication facts", activities: "Skip counting, times table games" },
        { week: 7, topic: "Fractions (Half, Quarter)", outcomes: "Identify and represent fractions", activities: "Fraction models, sharing equally" },
        { week: 8, topic: "Time (Half Hour)", outcomes: "Tell time to half hour", activities: "Clock activities, schedules" },
        { week: 9, topic: "Length (cm, m)", outcomes: "Measure in centimeters and meters", activities: "Measuring activities, estimation" },
        { week: 10, topic: "Review", outcomes: "Apply math skills", activities: "Problem solving, assessment" }
      ]
    },
    science: {
      term1: [
        { week: 1, topic: "States of Matter", outcomes: "Identify solids, liquids, gases", activities: "Sorting activities, experiments" },
        { week: 2, topic: "Changes of State", outcomes: "Describe melting and freezing", activities: "Ice experiments, observations" },
        { week: 3, topic: "Living Things - Classification", outcomes: "Classify animals by characteristics", activities: "Animal sorting, Venn diagrams" },
        { week: 4, topic: "Habitats", outcomes: "Describe different habitats", activities: "Habitat research, dioramas" },
        { week: 5, topic: "Food Chains", outcomes: "Understand simple food chains", activities: "Food chain models, games" },
        { week: 6, topic: "Simple Machines - Levers", outcomes: "Identify and use levers", activities: "Lever experiments, playground exploration" },
        { week: 7, topic: "Magnets", outcomes: "Explore magnetic properties", activities: "Magnet experiments, attraction/repulsion" },
        { week: 8, topic: "Light and Shadows", outcomes: "Understand how shadows form", activities: "Shadow puppets, light experiments" },
        { week: 9, topic: "Sound", outcomes: "Explore how sounds are made", activities: "Sound experiments, musical instruments" },
        { week: 10, topic: "Review", outcomes: "Demonstrate science learning", activities: "Science fair projects, assessment" }
      ]
    },
    livingInCommunity: {
      term1: [
        { week: 1, topic: "Rights and Responsibilities", outcomes: "Explain children's rights", activities: "Rights charter, responsibility matching" },
        { week: 2, topic: "Our Culture and Traditions", outcomes: "Describe cultural practices", activities: "Cultural sharing, tradition research" },
        { week: 3, topic: "Respecting Differences", outcomes: "Show respect for diversity", activities: "Diversity activities, cultural appreciation" },
        { week: 4, topic: "Our Local Community", outcomes: "Identify community features", activities: "Community mapping, local walk" },
        { week: 5, topic: "Community Services", outcomes: "Explain services in community", activities: "Service investigation, guest speakers" },
        { week: 6, topic: "Being a Good Citizen", outcomes: "Demonstrate citizenship", activities: "Community helper day, service project" },
        { week: 7, topic: "Decision Making", outcomes: "Make good decisions", activities: "Decision scenarios, consequence thinking" },
        { week: 8, topic: "Working Together", outcomes: "Collaborate effectively", activities: "Team challenges, group projects" },
        { week: 9, topic: "Conflict Resolution", outcomes: "Resolve conflicts peacefully", activities: "Mediation practice, peace circles" },
        { week: 10, topic: "Review", outcomes: "Demonstrate community values", activities: "Community project, assessment" }
      ]
    }
  },
  ...schemeOfWorkTemplatesYears4to6
};

// Printable Worksheets Templates
export const worksheetTemplates = {
  "Year 1": {
    languageCommunication: [
      {
        id: "ws-y1-lang-1",
        title: "Letter Tracing - Aa",
        type: "tracing",
        instructions: "Trace the letters carefully. Say the sound as you write.",
        content: "A a A a A a\na a a a a\nApple starts with A",
        answerKey: {
          description: "Students should trace letters A and a correctly with proper formation.",
          checkPoints: ["Capital A: Two diagonal lines meeting at top, horizontal line in middle", "Lowercase a: Circle with a vertical line on the right"]
        }
      },
      {
        id: "ws-y1-lang-2",
        title: "Picture Match",
        type: "matching",
        instructions: "Draw a line from each picture to its beginning letter.",
        items: ["Apple - A", "Ball - B", "Cat - C", "Dog - D"],
        answerKey: {
          answers: [
            { item: "🍎 Apple", correctLetter: "A" },
            { item: "⚽ Ball", correctLetter: "B" },
            { item: "🐱 Cat", correctLetter: "C" },
            { item: "🐕 Dog", correctLetter: "D" }
          ]
        }
      },
      {
        id: "ws-y1-lang-3",
        title: "Beginning Sounds",
        type: "phonics",
        instructions: "Circle the picture that starts with the given letter.",
        items: [
          { letter: "M", options: ["Moon", "Sun", "Car"], answer: "Moon" },
          { letter: "S", options: ["Dog", "Sun", "Ball"], answer: "Sun" },
          { letter: "T", options: ["Tree", "Fish", "Apple"], answer: "Tree" },
          { letter: "B", options: ["Cat", "Banana", "House"], answer: "Banana" }
        ],
        answerKey: {
          answers: ["M = Moon", "S = Sun", "T = Tree", "B = Banana"]
        }
      }
    ],
    mathematics: [
      {
        id: "ws-y1-math-1",
        title: "Counting Objects",
        type: "counting",
        instructions: "Count the objects and write the number.",
        content: "🐚🐚🐚 = ___\n🌺🌺🌺🌺🌺 = ___\n🐟🐟 = ___",
        answerKey: {
          answers: [
            { problem: "🐚🐚🐚", answer: "3" },
            { problem: "🌺🌺🌺🌺🌺", answer: "5" },
            { problem: "🐟🐟", answer: "2" }
          ]
        }
      },
      {
        id: "ws-y1-math-2",
        title: "Shape Coloring",
        type: "coloring",
        instructions: "Color the circles RED, squares BLUE, triangles GREEN",
        answerKey: {
          answers: ["Circles = Red", "Squares = Blue", "Triangles = Green"],
          note: "Check that students correctly identify each shape type"
        }
      },
      {
        id: "ws-y1-math-3",
        title: "Number Writing 1-10",
        type: "writing",
        instructions: "Write the missing numbers in the boxes.",
        items: ["1, 2, ___, 4, 5", "6, ___, 8, 9, ___", "___, 3, 4, ___, 6"],
        answerKey: {
          answers: [
            { sequence: "1, 2, ___, 4, 5", answer: "3" },
            { sequence: "6, ___, 8, 9, ___", answers: ["7", "10"] },
            { sequence: "___, 3, 4, ___, 6", answers: ["2", "5"] }
          ]
        }
      },
      {
        id: "ws-y1-math-4",
        title: "Simple Addition 0-10",
        type: "operations",
        instructions: "Solve the addition problems using counters or drawings.",
        problems: ["2 + 1 = ___", "3 + 2 = ___", "4 + 3 = ___", "5 + 2 = ___", "1 + 4 = ___"],
        answerKey: {
          answers: [
            { problem: "2 + 1", answer: "3" },
            { problem: "3 + 2", answer: "5" },
            { problem: "4 + 3", answer: "7" },
            { problem: "5 + 2", answer: "7" },
            { problem: "1 + 4", answer: "5" }
          ]
        }
      }
    ],
    science: [
      {
        id: "ws-y1-sci-1",
        title: "Living vs Non-Living",
        type: "sorting",
        instructions: "Circle the living things in GREEN. Circle the non-living things in RED.",
        items: ["🐕 Dog", "🪨 Rock", "🌸 Flower", "📚 Book", "🐟 Fish", "⚽ Ball"],
        answerKey: {
          living: ["🐕 Dog", "🌸 Flower", "🐟 Fish"],
          nonLiving: ["🪨 Rock", "📚 Book", "⚽ Ball"]
        }
      },
      {
        id: "ws-y1-sci-2",
        title: "Parts of a Plant",
        type: "labeling",
        instructions: "Write the correct label for each part of the plant.",
        items: ["Root", "Stem", "Leaf", "Flower"],
        answerKey: {
          answers: [
            { part: "Bottom underground part", answer: "Root" },
            { part: "Tall middle part", answer: "Stem" },
            { part: "Green flat parts on stem", answer: "Leaf" },
            { part: "Colorful part at top", answer: "Flower" }
          ]
        }
      }
    ]
  },
  "Year 2": {
    languageCommunication: [
      {
        id: "ws-y2-lang-1",
        title: "Sentence Building",
        type: "writing",
        instructions: "Put the words in order to make a sentence.",
        items: ["fish / The / swims", "plays / She / ball", "to / go / school / I"],
        answerKey: {
          answers: [
            { jumbled: "fish / The / swims", answer: "The fish swims." },
            { jumbled: "plays / She / ball", answer: "She plays ball." },
            { jumbled: "to / go / school / I", answer: "I go to school." }
          ]
        }
      },
      {
        id: "ws-y2-lang-2",
        title: "Rhyming Words",
        type: "phonics",
        instructions: "Match the words that rhyme.",
        items: [
          { word: "cat", options: ["dog", "hat", "sun"], answer: "hat" },
          { word: "sun", options: ["run", "ball", "tree"], answer: "run" },
          { word: "ball", options: ["cat", "tall", "fish"], answer: "tall" },
          { word: "tree", options: ["car", "bee", "hat"], answer: "bee" }
        ],
        answerKey: {
          answers: ["cat - hat", "sun - run", "ball - tall", "tree - bee"]
        }
      },
      {
        id: "ws-y2-lang-3",
        title: "Singular and Plural",
        type: "grammar",
        instructions: "Write the plural form of each word.",
        items: ["cat = ___", "dog = ___", "fish = ___", "tree = ___", "box = ___"],
        answerKey: {
          answers: [
            { singular: "cat", plural: "cats" },
            { singular: "dog", plural: "dogs" },
            { singular: "fish", plural: "fish" },
            { singular: "tree", plural: "trees" },
            { singular: "box", plural: "boxes" }
          ]
        }
      }
    ],
    mathematics: [
      {
        id: "ws-y2-math-1",
        title: "Addition Practice",
        type: "operations",
        instructions: "Solve the addition problems.",
        problems: ["23 + 14 = ___", "35 + 22 = ___", "41 + 18 = ___"],
        answerKey: {
          answers: [
            { problem: "23 + 14", answer: "37" },
            { problem: "35 + 22", answer: "57" },
            { problem: "41 + 18", answer: "59" }
          ]
        }
      },
      {
        id: "ws-y2-math-2",
        title: "Subtraction Practice",
        type: "operations",
        instructions: "Solve the subtraction problems.",
        problems: ["45 - 12 = ___", "67 - 25 = ___", "89 - 34 = ___", "50 - 23 = ___"],
        answerKey: {
          answers: [
            { problem: "45 - 12", answer: "33" },
            { problem: "67 - 25", answer: "42" },
            { problem: "89 - 34", answer: "55" },
            { problem: "50 - 23", answer: "27" }
          ]
        }
      },
      {
        id: "ws-y2-math-3",
        title: "Skip Counting",
        type: "patterns",
        instructions: "Complete the number patterns.",
        items: ["2, 4, 6, ___, ___", "5, 10, 15, ___, ___", "10, 20, 30, ___, ___"],
        answerKey: {
          answers: [
            { pattern: "2, 4, 6, ___, ___", answer: "8, 10" },
            { pattern: "5, 10, 15, ___, ___", answer: "20, 25" },
            { pattern: "10, 20, 30, ___, ___", answer: "40, 50" }
          ]
        }
      },
      {
        id: "ws-y2-math-4",
        title: "Place Value",
        type: "concepts",
        instructions: "Write the value of the underlined digit.",
        items: ["In 47, the 4 represents ___", "In 85, the 5 represents ___", "In 63, the 6 represents ___"],
        answerKey: {
          answers: [
            { number: "47", digit: "4", answer: "40 (4 tens)" },
            { number: "85", digit: "5", answer: "5 (5 ones)" },
            { number: "63", digit: "6", answer: "60 (6 tens)" }
          ]
        }
      }
    ],
    science: [
      {
        id: "ws-y2-sci-1",
        title: "Life Cycles",
        type: "sequencing",
        instructions: "Number the stages in the correct order.",
        items: ["Butterfly: egg, caterpillar, chrysalis, butterfly", "Frog: egg, tadpole, froglet, frog"],
        answerKey: {
          answers: [
            { animal: "Butterfly", order: ["1. Egg", "2. Caterpillar", "3. Chrysalis", "4. Butterfly"] },
            { animal: "Frog", order: ["1. Egg", "2. Tadpole", "3. Froglet", "4. Frog"] }
          ]
        }
      },
      {
        id: "ws-y2-sci-2",
        title: "Animal Habitats",
        type: "matching",
        instructions: "Match each animal to its habitat.",
        items: [
          { animal: "Fish", habitats: ["Ocean", "Desert", "Forest"], answer: "Ocean" },
          { animal: "Camel", habitats: ["Ocean", "Desert", "Forest"], answer: "Desert" },
          { animal: "Monkey", habitats: ["Ocean", "Desert", "Forest"], answer: "Forest" }
        ],
        answerKey: {
          answers: ["Fish - Ocean", "Camel - Desert", "Monkey - Forest"]
        }
      }
    ]
  },
  "Year 3": {
    languageCommunication: [
      {
        id: "ws-y3-lang-1",
        title: "English Words",
        type: "vocabulary",
        instructions: "Write the English word for each picture.",
        items: ["Fish = ___", "House = ___", "Tree = ___", "Water = ___"],
        answerKey: {
          answers: [
            { item: "🐟", answer: "Fish" },
            { item: "🏠", answer: "House" },
            { item: "🌳", answer: "Tree" },
            { item: "💧", answer: "Water" }
          ]
        }
      },
      {
        id: "ws-y3-lang-2",
        title: "Punctuation Practice",
        type: "grammar",
        instructions: "Add the correct punctuation mark at the end of each sentence.",
        items: [
          "What is your name___",
          "The sun is shining___",
          "Stop that___",
          "I like to swim___"
        ],
        answerKey: {
          answers: [
            { sentence: "What is your name", punctuation: "?", type: "question" },
            { sentence: "The sun is shining", punctuation: ".", type: "statement" },
            { sentence: "Stop that", punctuation: "!", type: "exclamation" },
            { sentence: "I like to swim", punctuation: ".", type: "statement" }
          ]
        }
      },
      {
        id: "ws-y3-lang-3",
        title: "Verb Tenses - Present and Past",
        type: "grammar",
        instructions: "Write the past tense of each verb.",
        items: ["walk - ___", "play - ___", "jump - ___", "eat - ___", "run - ___"],
        answerKey: {
          answers: [
            { present: "walk", past: "walked" },
            { present: "play", past: "played" },
            { present: "jump", past: "jumped" },
            { present: "eat", past: "ate" },
            { present: "run", past: "ran" }
          ]
        }
      }
    ],
    mathematics: [
      {
        id: "ws-y3-math-1",
        title: "Three-Digit Addition",
        type: "operations",
        instructions: "Solve the problems. Show your working.",
        problems: ["234 + 152 = ___", "345 + 231 = ___", "412 + 285 = ___"],
        answerKey: {
          answers: [
            { problem: "234 + 152", answer: "386", working: "234 + 152 = 386" },
            { problem: "345 + 231", answer: "576", working: "345 + 231 = 576" },
            { problem: "412 + 285", answer: "697", working: "412 + 285 = 697" }
          ]
        }
      },
      {
        id: "ws-y3-math-2",
        title: "Three-Digit Subtraction",
        type: "operations",
        instructions: "Solve the problems. Show your working.",
        problems: ["567 - 234 = ___", "823 - 411 = ___", "945 - 372 = ___"],
        answerKey: {
          answers: [
            { problem: "567 - 234", answer: "333" },
            { problem: "823 - 411", answer: "412" },
            { problem: "945 - 372", answer: "573" }
          ]
        }
      },
      {
        id: "ws-y3-math-3",
        title: "Multiplication Tables (2, 5, 10)",
        type: "operations",
        instructions: "Complete the multiplication facts.",
        problems: ["2 × 6 = ___", "5 × 4 = ___", "10 × 7 = ___", "2 × 9 = ___", "5 × 8 = ___"],
        answerKey: {
          answers: [
            { problem: "2 × 6", answer: "12" },
            { problem: "5 × 4", answer: "20" },
            { problem: "10 × 7", answer: "70" },
            { problem: "2 × 9", answer: "18" },
            { problem: "5 × 8", answer: "40" }
          ]
        }
      },
      {
        id: "ws-y3-math-4",
        title: "Word Problems",
        type: "word_problems",
        instructions: "Solve each word problem. Show your working.",
        problems: [
          "Maria has 125 mangoes. She sells 48. How many are left?",
          "Tom has 234 shells. He finds 156 more. How many in total?",
          "There are 5 bags with 10 coconuts each. How many coconuts altogether?"
        ],
        answerKey: {
          answers: [
            { problem: "Maria's mangoes", answer: "77 mangoes", working: "125 - 48 = 77" },
            { problem: "Tom's shells", answer: "390 shells", working: "234 + 156 = 390" },
            { problem: "Coconuts", answer: "50 coconuts", working: "5 × 10 = 50" }
          ]
        }
      }
    ],
    science: [
      {
        id: "ws-y3-sci-1",
        title: "States of Matter",
        type: "classification",
        instructions: "Classify each item as Solid, Liquid, or Gas.",
        items: ["Water", "Ice", "Steam", "Rock", "Juice", "Air"],
        answerKey: {
          solid: ["Ice", "Rock"],
          liquid: ["Water", "Juice"],
          gas: ["Steam", "Air"]
        }
      },
      {
        id: "ws-y3-sci-2",
        title: "Body Systems",
        type: "matching",
        instructions: "Match each organ to its system.",
        items: [
          { organ: "Heart", systems: ["Digestive", "Circulatory", "Respiratory"], answer: "Circulatory" },
          { organ: "Lungs", systems: ["Digestive", "Circulatory", "Respiratory"], answer: "Respiratory" },
          { organ: "Stomach", systems: ["Digestive", "Circulatory", "Respiratory"], answer: "Digestive" }
        ],
        answerKey: {
          answers: ["Heart - Circulatory System", "Lungs - Respiratory System", "Stomach - Digestive System"]
        }
      },
      {
        id: "ws-y3-sci-3",
        title: "Food Chains",
        type: "sequencing",
        instructions: "Arrange these in the correct food chain order.",
        items: ["Sun → ___ → ___ → ___", "Options: Grasshopper, Grass, Frog, Snake"],
        answerKey: {
          answer: "Sun → Grass → Grasshopper → Frog → Snake",
          explanation: "Energy flows from the sun to producers (plants), then to primary consumers, then to secondary consumers"
        }
      }
    ]
  },
  ...worksheetTemplatesYears4to6
};

// Lesson Plan Templates
export const lessonPlanTemplates = {
  "Year 1": {
    languageCommunication: [
      {
        id: "lp-y1-lang-1",
        week: 1,
        topic: "Introduction to School",
        duration: "40 minutes",
        objectives: [
          "Listen and follow simple classroom instructions",
          "Identify and say own name",
          "Recognize classroom objects"
        ],
        materials: ["Name tags", "Classroom objects", "Picture cards"],
        introduction: {
          time: "5 min",
          activity: "Teacher introduces self and welcomes students. Sing a welcome song together."
        },
        development: [
          { time: "10 min", activity: "Name Game: Each student says their name. Teacher repeats and class echoes." },
          { time: "10 min", activity: "Classroom Tour: Walk around identifying objects (desk, chair, board, door, window)." },
          { time: "10 min", activity: "Picture Matching: Match picture cards to real objects in classroom." }
        ],
        conclusion: {
          time: "5 min",
          activity: "Review: Point to objects as teacher calls out names. Sing goodbye song."
        },
        assessment: "Observation: Can students follow instructions and identify at least 3 classroom objects?",
        homework: "Draw your favorite thing in the classroom"
      },
      {
        id: "lp-y1-lang-2",
        week: 2,
        topic: "Letter Aa",
        duration: "40 minutes",
        objectives: [
          "Recognize uppercase and lowercase letter A",
          "Produce the /a/ sound correctly",
          "Identify words beginning with A"
        ],
        materials: ["Letter A flashcard", "Apple", "Picture cards", "Sand tray", "Crayons"],
        introduction: {
          time: "5 min",
          activity: "Show letter A flashcard. Say 'A says /a/ like apple'. Show real apple."
        },
        development: [
          { time: "8 min", activity: "Sound Practice: Repeat /a/ sound. Find A in alphabet chart." },
          { time: "10 min", activity: "Letter Formation: Trace A in the air, on desk, then in sand tray." },
          { time: "10 min", activity: "Word Hunt: Look at picture cards, identify items starting with A (apple, ant, arrow)." }
        ],
        conclusion: {
          time: "7 min",
          activity: "Letter A Song. Students trace A on paper and color apple picture."
        },
        assessment: "Can students form letter A correctly and identify /a/ sound?",
        homework: "Find 2 things at home that start with letter A"
      },
      {
        id: "lp-y1-lang-3",
        week: 3,
        topic: "Letter Bb",
        duration: "40 minutes",
        objectives: [
          "Recognize uppercase and lowercase letter B",
          "Produce the /b/ sound correctly",
          "Write letter B with correct formation"
        ],
        materials: ["Letter B flashcard", "Ball", "Banana picture", "Tracing sheets"],
        introduction: {
          time: "5 min",
          activity: "Review letter A. Introduce letter B with flashcard. 'B says /b/ like ball.'"
        },
        development: [
          { time: "10 min", activity: "Sound Recognition: Play 'B or Not B' game with pictures." },
          { time: "10 min", activity: "Letter Writing: Practice writing B on lined paper (big B, small b)." },
          { time: "10 min", activity: "Action Game: When teacher says B word, students bounce like a ball." }
        ],
        conclusion: {
          time: "5 min",
          activity: "Review A and B together. Letter sorting activity."
        },
        assessment: "Can students distinguish between A and B sounds and write letter B?",
        homework: "Practice writing B five times"
      }
    ],
    mathematics: [
      {
        id: "lp-y1-math-1",
        week: 1,
        topic: "Counting 1-5",
        duration: "40 minutes",
        objectives: [
          "Count objects from 1 to 5",
          "Recognize numerals 1-5",
          "Match quantity to numeral"
        ],
        materials: ["Counting objects (shells, stones)", "Number cards 1-5", "Number song chart"],
        introduction: {
          time: "5 min",
          activity: "Sing counting song 1-5. Show fingers as you count."
        },
        development: [
          { time: "10 min", activity: "Concrete Counting: Give students shells. Count together 1, 2, 3, 4, 5." },
          { time: "10 min", activity: "Number Recognition: Show number cards. Students hold up matching fingers." },
          { time: "10 min", activity: "Matching Game: Place objects in groups. Match to correct number card." }
        ],
        conclusion: {
          time: "5 min",
          activity: "Quick count around the room (windows, doors, chairs). Counting song."
        },
        assessment: "Can students count 5 objects and match to correct numeral?",
        homework: "Count things in your home (how many cups, plates)"
      },
      {
        id: "lp-y1-math-2",
        week: 2,
        topic: "Counting 6-10",
        duration: "40 minutes",
        objectives: [
          "Count objects from 6 to 10",
          "Recognize numerals 6-10",
          "Count forward from any number to 10"
        ],
        materials: ["Counting sticks", "Number cards 1-10", "Ten-frame cards"],
        introduction: {
          time: "5 min",
          activity: "Review 1-5. Introduce 'Now we learn bigger numbers!' Count fingers on both hands."
        },
        development: [
          { time: "12 min", activity: "Ten-Frame: Show how 6 is 5 and 1 more. Fill ten-frame to 10." },
          { time: "10 min", activity: "Counting Train: Students line up. Count to 10. Who is number 7?" },
          { time: "8 min", activity: "Number Ordering: Put jumbled cards 1-10 in correct order." }
        ],
        conclusion: {
          time: "5 min",
          activity: "Count backward from 10. Blast off! Counting song 1-10."
        },
        assessment: "Can students count to 10 and identify numerals 6-10?",
        homework: "Count to 10 with family members"
      }
    ],
    science: [
      {
        id: "lp-y1-sci-1",
        week: 1,
        topic: "My Body Parts",
        duration: "40 minutes",
        objectives: [
          "Name basic body parts (head, hands, feet, eyes, ears, nose, mouth)",
          "Point to body parts when named",
          "Understand that our body helps us do things"
        ],
        materials: ["Body parts poster", "Mirror", "Picture cards"],
        introduction: {
          time: "5 min",
          activity: "Sing 'Head, Shoulders, Knees and Toes'. Touch each part as we sing."
        },
        development: [
          { time: "10 min", activity: "Body Part Hunt: Teacher calls body part, students touch it. Use poster to show." },
          { time: "10 min", activity: "Mirror Activity: Look in mirror, point to your eyes, nose, mouth. Count fingers." },
          { time: "10 min", activity: "Matching Game: Match picture cards of body parts to poster." }
        ],
        conclusion: {
          time: "5 min",
          activity: "Draw yourself. Label head, hands, feet. Sing body parts song again."
        },
        assessment: "Can students identify and point to at least 5 body parts?",
        homework: "Tell family members the names of your body parts"
      },
      {
        id: "lp-y1-sci-2",
        week: 2,
        topic: "My Five Senses",
        duration: "40 minutes",
        objectives: [
          "Name the five senses (see, hear, smell, taste, touch)",
          "Match senses to body parts",
          "Explore using different senses"
        ],
        materials: ["Sensory items (fruit, bell, flower, soft cloth)", "Blindfold", "Picture cards"],
        introduction: {
          time: "5 min",
          activity: "Show pictures of eyes, ears, nose, tongue, hands. 'These help us learn about the world!'"
        },
        development: [
          { time: "12 min", activity: "Sense Stations: Rotate through - smell flower, touch fabric, taste fruit, hear bell, look at pictures." },
          { time: "10 min", activity: "Guessing Game: Blindfold volunteer. Can they guess object using only touch or smell?" },
          { time: "8 min", activity: "Sense Sorting: Sort picture cards by which sense we use (ears for music, eyes for rainbow)." }
        ],
        conclusion: {
          time: "5 min",
          activity: "Five Senses Song. Review: 'What do we use to see? To hear?'"
        },
        assessment: "Can students name 5 senses and match to correct body part?",
        homework: "Find something at home that smells nice"
      }
    ],
    livingInCommunity: [
      {
        id: "lp-y1-lic-1",
        week: 1,
        topic: "My Family",
        duration: "40 minutes",
        objectives: [
          "Name family members (mother, father, brother, sister, grandmother, grandfather)",
          "Talk about their own family",
          "Understand that families can be different"
        ],
        materials: ["Family photos", "Picture cards", "Drawing paper", "Crayons"],
        introduction: {
          time: "5 min",
          activity: "Show pictures of different families. 'Families are people who love and care for us.'"
        },
        development: [
          { time: "10 min", activity: "Family Tree: Teacher shows example. Students name their family members." },
          { time: "12 min", activity: "Draw My Family: Students draw their family. Label each person (Mama, Papa, etc.)." },
          { time: "8 min", activity: "Sharing Circle: Volunteers share their family drawing. 'This is my...'." }
        ],
        conclusion: {
          time: "5 min",
          activity: "Family Song. Discussion: 'What makes families special?'"
        },
        assessment: "Can students name at least 3 family members and describe their family?",
        homework: "Ask a family member to tell you a story about when they were young"
      },
      {
        id: "lp-y1-lic-2",
        week: 2,
        topic: "Being Healthy and Clean",
        duration: "40 minutes",
        objectives: [
          "Understand importance of washing hands",
          "Learn steps for proper handwashing",
          "Know when to wash hands"
        ],
        materials: ["Soap", "Water basin", "Towel", "Poster of handwashing steps"],
        introduction: {
          time: "5 min",
          activity: "Show dirty hands. 'Can we see germs? No! But they can make us sick. How do we get rid of them?'"
        },
        development: [
          { time: "10 min", activity: "Demonstration: Teacher shows 6 steps of handwashing. Students mime along." },
          { time: "12 min", activity: "Practice Time: In small groups, students wash hands properly with soap and water." },
          { time: "8 min", activity: "When to Wash: Role play - before eating, after toilet, after playing outside." }
        ],
        conclusion: {
          time: "5 min",
          activity: "Handwashing Song (to 'Happy Birthday' tune). Review steps together."
        },
        assessment: "Can students demonstrate proper handwashing technique?",
        homework: "Wash hands before every meal and after using the toilet"
      },
      {
        id: "lp-y1-lic-3",
        week: 3,
        topic: "Helpers in Our Community",
        duration: "40 minutes",
        objectives: [
          "Identify community helpers (teacher, nurse, police, farmer)",
          "Understand how helpers serve the community",
          "Show respect for helpers"
        ],
        materials: ["Picture cards of helpers", "Props (stethoscope, hat)", "Story book"],
        introduction: {
          time: "5 min",
          activity: "Who helps us? Teacher helps us learn. Who else helps in our village/town?"
        },
        development: [
          { time: "10 min", activity: "Helper Pictures: Show and discuss different helpers. What do they do?" },
          { time: "12 min", activity: "Role Play: Students pretend to be different helpers. Others guess who they are." },
          { time: "8 min", activity: "Thank You Cards: Draw a helper and write 'Thank You' (with teacher help)." }
        ],
        conclusion: {
          time: "5 min",
          activity: "Community Helper Song. 'How can we help our community?'"
        },
        assessment: "Can students name 3 community helpers and explain what they do?",
        homework: "Talk to a community helper and learn about their job"
      }
    ]
  },
  "Year 2": {
    languageCommunication: [
      {
        id: "lp-y2-lang-1",
        week: 1,
        topic: "Reading Fluency - Short Sentences",
        duration: "45 minutes",
        objectives: [
          "Read simple sentences with appropriate speed",
          "Use picture cues to understand meaning",
          "Retell what was read"
        ],
        materials: ["Big Book", "Sentence strips", "Picture cards"],
        introduction: {
          time: "5 min",
          activity: "Show Big Book cover. Predict what story is about from pictures."
        },
        development: [
          { time: "12 min", activity: "Shared Reading: Teacher reads aloud, pointing to words. Students echo-read." },
          { time: "10 min", activity: "Sentence Practice: Read sentence strips together. Match sentences to pictures." },
          { time: "10 min", activity: "Partner Reading: Pairs take turns reading sentences to each other." }
        ],
        conclusion: {
          time: "8 min",
          activity: "Retelling: What happened in the story? Draw favorite part."
        },
        assessment: "Can students read simple sentences and retell basic events?",
        homework: "Read story to family member"
      }
    ],
    mathematics: [
      {
        id: "lp-y2-math-1",
        week: 1,
        topic: "Two-Digit Addition without Regrouping",
        duration: "45 minutes",
        objectives: [
          "Add two 2-digit numbers without regrouping",
          "Use place value understanding",
          "Check answers using counting"
        ],
        materials: ["Base-10 blocks", "Place value mats", "Whiteboards"],
        introduction: {
          time: "5 min",
          activity: "Review place value: tens and ones. Show 23 with blocks."
        },
        development: [
          { time: "15 min", activity: "Concrete: Add 23 + 14 using blocks. Combine tens, combine ones. Write answer." },
          { time: "10 min", activity: "Pictorial: Draw tens (sticks) and ones (dots) to solve problems." },
          { time: "10 min", activity: "Abstract: Practice written addition on whiteboards." }
        ],
        conclusion: {
          time: "5 min",
          activity: "Partner check: Exchange answers and verify with blocks."
        },
        assessment: "Can students correctly add two 2-digit numbers?",
        homework: "Complete 5 addition problems"
      }
    ],
    science: [
      {
        id: "lp-y2-sci-1",
        week: 1,
        topic: "Plants and Their Parts",
        duration: "45 minutes",
        objectives: [
          "Name parts of a plant (root, stem, leaf, flower)",
          "Understand what each part does",
          "Observe real plants"
        ],
        materials: ["Real plant with roots", "Plant diagram", "Magnifying glasses", "Drawing paper"],
        introduction: {
          time: "5 min",
          activity: "Show real plant. 'Plants are living things. Let's learn about their parts!'"
        },
        development: [
          { time: "12 min", activity: "Plant Parts Tour: Point to each part. Root drinks water, stem holds up, leaves make food, flower makes seeds." },
          { time: "12 min", activity: "Observation Walk: Go outside, look at different plants with magnifying glasses. Find all parts." },
          { time: "10 min", activity: "Draw and Label: Draw a plant with all 4 parts labeled." }
        ],
        conclusion: {
          time: "6 min",
          activity: "Plant Parts Song. Quiz: 'Which part drinks water? Which part makes food?'"
        },
        assessment: "Can students name and explain function of 4 plant parts?",
        homework: "Find a plant at home and identify its parts"
      },
      {
        id: "lp-y2-sci-2",
        week: 2,
        topic: "What Plants Need to Grow",
        duration: "45 minutes",
        objectives: [
          "Identify what plants need (water, sunlight, soil, air)",
          "Predict what happens without these needs",
          "Begin to grow a plant"
        ],
        materials: ["Seeds", "Pots", "Soil", "Water", "Picture cards"],
        introduction: {
          time: "5 min",
          activity: "Review plant parts. 'What do plants need to be healthy and grow? Let's find out!'"
        },
        development: [
          { time: "10 min", activity: "Discussion: Show pictures of healthy vs unhealthy plants. What's different? (no water, no sun)" },
          { time: "15 min", activity: "Planting Activity: Each student plants a seed in a pot. Add soil, water, place in sunlight." },
          { time: "10 min", activity: "Prediction: Draw what your plant will look like in 2 weeks. What do we need to do to help it grow?" }
        ],
        conclusion: {
          time: "5 min",
          activity: "Create class plant care chart. Review 4 things plants need."
        },
        assessment: "Can students name 4 things plants need to grow?",
        homework: "Water your plant and observe it every day"
      }
    ],
    livingInCommunity: [
      {
        id: "lp-y2-lic-1",
        week: 1,
        topic: "Rules at Home and School",
        duration: "45 minutes",
        objectives: [
          "Understand why we have rules",
          "Identify rules at home and school",
          "Follow classroom rules"
        ],
        materials: ["Rules poster", "Picture cards", "Role play props"],
        introduction: {
          time: "5 min",
          activity: "What would happen if there were no rules? Discuss scenarios (everyone talking at once, pushing)."
        },
        development: [
          { time: "12 min", activity: "Classroom Rules: List our rules together. Why is each important? (Raise hand - so everyone can be heard)" },
          { time: "10 min", activity: "Home Rules: Share rules at home (bedtime, chores, respect elders). Similarities to school rules?" },
          { time: "12 min", activity: "Role Play: Act out following rules vs breaking rules. How does it feel when rules are followed?" }
        ],
        conclusion: {
          time: "6 min",
          activity: "Create class rules poster together. Sign our names as promise to follow."
        },
        assessment: "Can students explain why rules are important and name 3 school rules?",
        homework: "List 3 rules you follow at home"
      },
      {
        id: "lp-y2-lic-2",
        week: 2,
        topic: "Keeping Safe",
        duration: "45 minutes",
        objectives: [
          "Identify safe and unsafe situations",
          "Know what to do in emergencies",
          "Practice safety rules"
        ],
        materials: ["Safety pictures", "Emergency number poster", "Role play scenarios"],
        introduction: {
          time: "5 min",
          activity: "What does 'safe' mean? When do you feel safe? When might you feel unsafe?"
        },
        development: [
          { time: "10 min", activity: "Safe or Unsafe: Show picture cards. Students sort into safe/unsafe. Discuss why." },
          { time: "12 min", activity: "Safety Rules: Road safety (look both ways), water safety (never swim alone), stranger safety (tell trusted adult)." },
          { time: "12 min", activity: "Emergency Practice: What to do if there's a fire? If someone is hurt? Practice 'stop, drop, roll'." }
        ],
        conclusion: {
          time: "6 min",
          activity: "Safety pledge: 'I promise to keep myself safe by...' Draw one safety rule."
        },
        assessment: "Can students identify unsafe situations and explain what to do?",
        homework: "Discuss emergency plan with family"
      },
      {
        id: "lp-y2-lic-3",
        week: 3,
        topic: "Our Environment",
        duration: "45 minutes",
        objectives: [
          "Understand what environment means",
          "Identify ways to care for our environment",
          "Practice keeping our school clean"
        ],
        materials: ["Pictures of clean/dirty environments", "Rubbish bags", "Recycling bins"],
        introduction: {
          time: "5 min",
          activity: "Show two pictures: clean beach vs dirty beach. 'Which looks better? Which is healthier?'"
        },
        development: [
          { time: "10 min", activity: "What is Environment: The air, water, land around us. Living things (plants, animals) and non-living things." },
          { time: "12 min", activity: "Ways to Care: Don't litter, use less plastic, plant trees, save water. Discuss local examples." },
          { time: "12 min", activity: "Clean Up Activity: Go outside and pick up rubbish around school. Sort into bins." }
        ],
        conclusion: {
          time: "6 min",
          activity: "Environment Promise: 'I will help keep our environment clean by...' Share ideas."
        },
        assessment: "Can students name 3 ways to care for the environment?",
        homework: "Do one thing at home to help the environment"
      }
    ]
  },
  "Year 3": {
    languageCommunication: [
      {
        id: "lp-y3-lang-1",
        week: 1,
        topic: "Introduction to English Vocabulary",
        duration: "45 minutes",
        objectives: [
          "Learn basic English greetings",
          "Identify common English words",
          "Respond to simple English questions"
        ],
        materials: ["English vocabulary cards", "Audio recordings", "Poster of greetings"],
        introduction: {
          time: "5 min",
          activity: "Teacher greets in English: 'Good morning! How are you?' Explain we are learning English."
        },
        development: [
          { time: "12 min", activity: "Greetings Practice: Hello, Good morning, Good afternoon. Students repeat and practice with partners." },
          { time: "12 min", activity: "Vocabulary Introduction: Learn 10 common words (cat, dog, fish, house, tree, water, food, book, pen, school)." },
          { time: "10 min", activity: "Picture Match: Match English words to pictures. Say words aloud." }
        ],
        conclusion: {
          time: "6 min",
          activity: "Goodbye practice: 'Goodbye! See you tomorrow!' Review words with flashcards."
        },
        assessment: "Can students respond to 'Hello' and 'How are you?' and identify 5 vocabulary words?",
        homework: "Practice greetings with family"
      }
    ],
    mathematics: [
      {
        id: "lp-y3-math-1",
        week: 1,
        topic: "Three-Digit Numbers",
        duration: "45 minutes",
        objectives: [
          "Read and write numbers to 500",
          "Identify place value (hundreds, tens, ones)",
          "Compare three-digit numbers"
        ],
        materials: ["Place value charts", "Number cards", "Base-10 blocks"],
        introduction: {
          time: "5 min",
          activity: "Review two-digit numbers. 'Today we learn about hundreds!'"
        },
        development: [
          { time: "15 min", activity: "Building Numbers: Use blocks to show 234. Count: 2 hundreds, 3 tens, 4 ones." },
          { time: "12 min", activity: "Place Value Game: Teacher says number, students build with blocks and write." },
          { time: "8 min", activity: "Comparing: Use < > = to compare pairs of three-digit numbers." }
        ],
        conclusion: {
          time: "5 min",
          activity: "Quick quiz: Write the number I say (three hundred forty-two)."
        },
        assessment: "Can students read, write, and compare three-digit numbers?",
        homework: "Write numbers 100 to 150"
      }
    ],
    science: [
      {
        id: "lp-y3-sci-1",
        week: 1,
        topic: "States of Matter",
        duration: "45 minutes",
        objectives: [
          "Identify three states of matter (solid, liquid, gas)",
          "Give examples of each state",
          "Understand that matter can change states"
        ],
        materials: ["Ice cubes", "Water", "Balloon", "Various objects (rock, juice, air in balloon)"],
        introduction: {
          time: "5 min",
          activity: "Hold up ice, water, and balloon. 'These are all made of matter. But they are different. How?'"
        },
        development: [
          { time: "12 min", activity: "Solid Exploration: Touch rock, wood, ice. What do they have in common? (keep shape, hard)" },
          { time: "12 min", activity: "Liquid Exploration: Pour water, juice. What happens? (takes shape of container, flows)" },
          { time: "10 min", activity: "Gas Exploration: Feel air from balloon. Can you see it? (invisible, fills space)" }
        ],
        conclusion: {
          time: "6 min",
          activity: "Sorting Game: Sort picture cards into solid, liquid, gas. Share answers."
        },
        assessment: "Can students identify and give examples of solids, liquids, and gases?",
        homework: "List 5 solids, 3 liquids, and 2 gases you find at home"
      },
      {
        id: "lp-y3-sci-2",
        week: 2,
        topic: "Simple Machines - Levers",
        duration: "45 minutes",
        objectives: [
          "Understand what a lever is",
          "Identify levers in everyday life",
          "Experiment with how levers make work easier"
        ],
        materials: ["Ruler", "Pencil (fulcrum)", "Small weights", "Pictures of seesaws, scissors"],
        introduction: {
          time: "5 min",
          activity: "Have you been on a seesaw? How does it work? Today we learn about levers!"
        },
        development: [
          { time: "15 min", activity: "Build a Lever: Use ruler and pencil as fulcrum. Try to lift weight. Move fulcrum - what happens?" },
          { time: "10 min", activity: "Lever Examples: Look at pictures - seesaw, scissors, bottle opener. Find the fulcrum in each." },
          { time: "10 min", activity: "Lever Hunt: What levers are in our classroom? (door handle, stapler, scissors)" }
        ],
        conclusion: {
          time: "5 min",
          activity: "Draw a lever you use at home. Label the parts (lever, fulcrum, load)."
        },
        assessment: "Can students explain how a lever works and identify 3 examples?",
        homework: "Find 3 levers at home and draw them"
      }
    ],
    livingInCommunity: [
      {
        id: "lp-y3-lic-1",
        week: 1,
        topic: "Rights and Responsibilities",
        duration: "45 minutes",
        objectives: [
          "Understand the difference between rights and responsibilities",
          "Identify children's rights",
          "Understand that rights come with responsibilities"
        ],
        materials: ["Rights and responsibilities cards", "Poster paper", "Markers"],
        introduction: {
          time: "5 min",
          activity: "What is a 'right'? (something you deserve) What is a 'responsibility'? (something you must do) Examples?"
        },
        development: [
          { time: "12 min", activity: "Children's Rights: Learn about right to education, food, safety, play. Discuss what each means." },
          { time: "12 min", activity: "Matching Game: Match rights to responsibilities. (Right to learn → Responsibility to study)" },
          { time: "10 min", activity: "Create Poster: In groups, make poster showing one right and its responsibility." }
        ],
        conclusion: {
          time: "6 min",
          activity: "Gallery Walk: View other groups' posters. Discuss: 'What happens if we only take rights but not responsibilities?'"
        },
        assessment: "Can students explain 3 rights and their matching responsibilities?",
        homework: "Interview family: 'What rights and responsibilities do you have?'"
      },
      {
        id: "lp-y3-lic-2",
        week: 2,
        topic: "Our Culture and Traditions",
        duration: "45 minutes",
        objectives: [
          "Understand what culture means",
          "Share traditions from their community",
          "Respect different cultures"
        ],
        materials: ["Traditional items", "Pictures of cultural practices", "Map of Vanuatu"],
        introduction: {
          time: "5 min",
          activity: "What makes Vanuatu special? Our languages, customs, dances, food - this is our culture!"
        },
        development: [
          { time: "12 min", activity: "Cultural Sharing: Students share a tradition from their family or island. (special food, dance, custom)" },
          { time: "12 min", activity: "Cultural Items: Show traditional items (basket, tapa cloth, shell money). Discuss their importance." },
          { time: "10 min", activity: "Respect Discussion: Different islands have different customs. Why is it important to respect all cultures?" }
        ],
        conclusion: {
          time: "6 min",
          activity: "Draw or write about your favorite tradition. Share: 'I am proud of my culture because...'"
        },
        assessment: "Can students describe their own cultural traditions and show respect for others?",
        homework: "Learn one traditional story from your grandparents"
      },
      {
        id: "lp-y3-lic-3",
        week: 3,
        topic: "Working Together",
        duration: "45 minutes",
        objectives: [
          "Understand the importance of teamwork",
          "Practice cooperation skills",
          "Solve problems as a group"
        ],
        materials: ["Building blocks", "Large puzzle", "Team challenge materials"],
        introduction: {
          time: "5 min",
          activity: "Can one person build a house alone? Why do villages work together? Today we practice teamwork!"
        },
        development: [
          { time: "15 min", activity: "Team Challenge: Groups build tallest tower with blocks. Rules: everyone must contribute, be kind." },
          { time: "12 min", activity: "Puzzle Race: Teams complete puzzle together. What strategies helped? (taking turns, helping each other)" },
          { time: "8 min", activity: "Reflection: What was hard about teamwork? What made it successful?" }
        ],
        conclusion: {
          time: "5 min",
          activity: "Teamwork Pledge: 'When I work with others, I will...' Share ideas as a class."
        },
        assessment: "Can students demonstrate cooperation and explain why teamwork is important?",
        homework: "Work with family on a task together (cooking, cleaning, gardening)"
      }
    ]
  },
  ...lessonPlanTemplatesYears4to6
};

// Test Blueprint Templates
export const testBlueprintTemplates = {
  "Year 1": {
    languageCommunication: {
      term1: {
        title: "Term 1 Language Assessment Blueprint",
        totalMarks: 30,
        duration: "30 minutes",
        sections: [
          {
            name: "Letter Recognition",
            marks: 10,
            questions: 10,
            description: "Identify uppercase and lowercase letters A-I",
            sampleItems: ["Circle the letter A", "Match uppercase to lowercase", "Point to letter B"]
          },
          {
            name: "Phonics",
            marks: 10,
            questions: 5,
            description: "Identify beginning sounds of words",
            sampleItems: ["What sound does 'apple' start with?", "Find pictures that start with /b/"]
          },
          {
            name: "Listening Comprehension",
            marks: 10,
            questions: 5,
            description: "Follow instructions and answer questions about a short story",
            sampleItems: ["Draw what teacher says", "Point to the character mentioned"]
          }
        ],
        rubric: {
          excellent: { range: "25-30", description: "Student demonstrates strong letter and sound knowledge" },
          good: { range: "18-24", description: "Student shows good understanding with minor gaps" },
          developing: { range: "10-17", description: "Student needs additional support" },
          beginning: { range: "0-9", description: "Student requires significant intervention" }
        }
      },
      term2: {
        title: "Term 2 Language Assessment Blueprint",
        totalMarks: 40,
        duration: "35 minutes",
        sections: [
          {
            name: "Letter Recognition (J-Z)",
            marks: 13,
            questions: 13,
            description: "Identify remaining letters of the alphabet",
            sampleItems: ["Circle letter M", "Write the letter that comes after N"]
          },
          {
            name: "Word Reading",
            marks: 12,
            questions: 6,
            description: "Read simple CVC words",
            sampleItems: ["Read: cat, dog, sun", "Match word to picture"]
          },
          {
            name: "Name Writing",
            marks: 5,
            questions: 1,
            description: "Write own name correctly",
            sampleItems: ["Write your name on the line"]
          },
          {
            name: "Story Comprehension",
            marks: 10,
            questions: 5,
            description: "Answer questions about a read-aloud story",
            sampleItems: ["Who was in the story?", "What happened first?"]
          }
        ],
        rubric: {
          excellent: { range: "34-40", description: "Exceeds expectations" },
          good: { range: "25-33", description: "Meets expectations" },
          developing: { range: "15-24", description: "Approaching expectations" },
          beginning: { range: "0-14", description: "Below expectations" }
        }
      }
    },
    mathematics: {
      term1: {
        title: "Term 1 Mathematics Assessment Blueprint",
        totalMarks: 30,
        duration: "30 minutes",
        sections: [
          {
            name: "Counting",
            marks: 10,
            questions: 5,
            description: "Count objects and write numerals 1-10",
            sampleItems: ["Count the shells", "Write how many", "Circle the group with 5"]
          },
          {
            name: "Number Recognition",
            marks: 10,
            questions: 10,
            description: "Identify numerals 1-10",
            sampleItems: ["Point to number 7", "Match numeral to word"]
          },
          {
            name: "Shapes",
            marks: 10,
            questions: 5,
            description: "Identify and name basic shapes",
            sampleItems: ["Color the circles red", "How many sides does a square have?"]
          }
        ],
        rubric: {
          excellent: { range: "25-30", description: "Strong number sense and shape knowledge" },
          good: { range: "18-24", description: "Good understanding of concepts" },
          developing: { range: "10-17", description: "Developing number and shape concepts" },
          beginning: { range: "0-9", description: "Needs foundational support" }
        }
      }
    }
  },
  "Year 2": {
    languageCommunication: {
      term1: {
        title: "Term 1 Language Assessment Blueprint",
        totalMarks: 50,
        duration: "40 minutes",
        sections: [
          {
            name: "Reading Fluency",
            marks: 15,
            questions: 3,
            description: "Read sentences with appropriate speed and accuracy",
            sampleItems: ["Read the passage aloud", "Read with expression"]
          },
          {
            name: "Reading Comprehension",
            marks: 15,
            questions: 5,
            description: "Answer questions about a short text",
            sampleItems: ["What is the main idea?", "Who is the main character?", "What happened at the end?"]
          },
          {
            name: "Writing",
            marks: 20,
            questions: 2,
            description: "Write simple sentences and short texts",
            sampleItems: ["Write 3 sentences about your family", "Complete the story"]
          }
        ],
        rubric: {
          excellent: { range: "42-50", description: "Fluent reader and confident writer" },
          good: { range: "30-41", description: "Competent reading and writing skills" },
          developing: { range: "18-29", description: "Developing literacy skills" },
          beginning: { range: "0-17", description: "Needs intensive support" }
        }
      }
    },
    mathematics: {
      term1: {
        title: "Term 1 Mathematics Assessment Blueprint",
        totalMarks: 50,
        duration: "40 minutes",
        sections: [
          {
            name: "Number Operations",
            marks: 20,
            questions: 10,
            description: "Addition and subtraction of two-digit numbers",
            sampleItems: ["23 + 14 = ", "45 - 23 = ", "Word problem: Add the mangoes"]
          },
          {
            name: "Place Value",
            marks: 15,
            questions: 5,
            description: "Understand tens and ones",
            sampleItems: ["What is the value of 3 in 35?", "Write fifty-six as a number"]
          },
          {
            name: "Measurement",
            marks: 15,
            questions: 5,
            description: "Measure using standard units",
            sampleItems: ["Measure the line in cm", "Which is longer?"]
          }
        ],
        rubric: {
          excellent: { range: "42-50", description: "Strong mathematical understanding" },
          good: { range: "30-41", description: "Good grasp of concepts" },
          developing: { range: "18-29", description: "Developing mathematical skills" },
          beginning: { range: "0-17", description: "Needs additional support" }
        }
      }
    }
  },
  "Year 3": {
    languageCommunication: {
      term1: {
        title: "Term 1 English Introduction Assessment Blueprint",
        totalMarks: 40,
        duration: "35 minutes",
        sections: [
          {
            name: "English Vocabulary",
            marks: 15,
            questions: 15,
            description: "Identify basic English words",
            sampleItems: ["Match English word to picture", "Write the English word for the picture"]
          },
          {
            name: "Listening (English)",
            marks: 10,
            questions: 5,
            description: "Follow simple English instructions",
            sampleItems: ["Draw what teacher says in English", "Point to the correct picture"]
          },
          {
            name: "Speaking (English)",
            marks: 15,
            questions: 3,
            description: "Respond to simple English questions",
            sampleItems: ["Answer: What is your name?", "Say: Good morning"]
          }
        ],
        rubric: {
          excellent: { range: "34-40", description: "Ready for English literacy development" },
          good: { range: "25-33", description: "Good foundation in English basics" },
          developing: { range: "15-24", description: "Building English vocabulary" },
          beginning: { range: "0-14", description: "Needs English language support" }
        }
      }
    },
    mathematics: {
      term1: {
        title: "Term 1 Mathematics Assessment Blueprint",
        totalMarks: 60,
        duration: "45 minutes",
        sections: [
          {
            name: "Three-Digit Numbers",
            marks: 20,
            questions: 10,
            description: "Read, write, and compare numbers to 500",
            sampleItems: ["Write 'three hundred twenty-four'", "Compare: 456 ___ 465"]
          },
          {
            name: "Addition & Subtraction",
            marks: 25,
            questions: 10,
            description: "Operations with three-digit numbers",
            sampleItems: ["234 + 152 = ", "345 - 123 = ", "Word problem with regrouping"]
          },
          {
            name: "Problem Solving",
            marks: 15,
            questions: 3,
            description: "Apply mathematical concepts to real-world problems",
            sampleItems: ["At the market...", "How much change?"]
          }
        ],
        rubric: {
          excellent: { range: "50-60", description: "Advanced mathematical reasoning" },
          good: { range: "36-49", description: "Solid mathematical foundation" },
          developing: { range: "22-35", description: "Developing mathematical competence" },
          beginning: { range: "0-21", description: "Requires mathematical intervention" }
        }
      }
    }
  },
  ...testBlueprintTemplatesYears4to6
};

// Complete Test Papers with Answer Keys
export const testPapers = {
  "Year 1": {
    languageCommunication: {
      term1: {
        id: "y1-lang-t1",
        title: "Year 1 Language & Communication - Term 1 Test",
        subject: "Language and Communication",
        totalMarks: 30,
        duration: "30 minutes",
        instructions: [
          "Write your name on the paper.",
          "Listen carefully to the teacher.",
          "Circle or write your answers clearly.",
          "Ask the teacher if you need help."
        ],
        sections: [
          {
            name: "Section A: Letter Recognition",
            marks: 10,
            instructions: "Circle the correct letter.",
            questions: [
              { id: 1, question: "Circle the letter A", options: ["A", "B", "C"], answer: "A", marks: 1 },
              { id: 2, question: "Circle the letter B", options: ["D", "B", "E"], answer: "B", marks: 1 },
              { id: 3, question: "Circle the letter C", options: ["C", "G", "O"], answer: "C", marks: 1 },
              { id: 4, question: "Circle the letter D", options: ["B", "P", "D"], answer: "D", marks: 1 },
              { id: 5, question: "Circle the letter E", options: ["F", "E", "H"], answer: "E", marks: 1 },
              { id: 6, question: "Circle lowercase 'a'", options: ["a", "d", "o"], answer: "a", marks: 1 },
              { id: 7, question: "Circle lowercase 'b'", options: ["d", "b", "p"], answer: "b", marks: 1 },
              { id: 8, question: "Circle lowercase 'c'", options: ["o", "e", "c"], answer: "c", marks: 1 },
              { id: 9, question: "Match: A goes with", options: ["a", "b", "c"], answer: "a", marks: 1 },
              { id: 10, question: "Match: B goes with", options: ["d", "b", "a"], answer: "b", marks: 1 }
            ]
          },
          {
            name: "Section B: Beginning Sounds",
            marks: 10,
            instructions: "What sound does the picture start with? Circle the letter.",
            questions: [
              { id: 11, question: "🍎 Apple starts with", options: ["A", "B", "C"], answer: "A", marks: 2 },
              { id: 12, question: "🏀 Ball starts with", options: ["D", "B", "P"], answer: "B", marks: 2 },
              { id: 13, question: "🐱 Cat starts with", options: ["K", "C", "S"], answer: "C", marks: 2 },
              { id: 14, question: "🐕 Dog starts with", options: ["B", "G", "D"], answer: "D", marks: 2 },
              { id: 15, question: "🥚 Egg starts with", options: ["E", "A", "I"], answer: "E", marks: 2 }
            ]
          },
          {
            name: "Section C: Following Instructions",
            marks: 10,
            instructions: "Listen to the teacher and do what they say.",
            questions: [
              { id: 16, question: "Draw a circle", type: "drawing", answer: "Circle shape drawn", marks: 2 },
              { id: 17, question: "Draw a square", type: "drawing", answer: "Square shape drawn", marks: 2 },
              { id: 18, question: "Write the letter A", type: "writing", answer: "A", marks: 2 },
              { id: 19, question: "Write your name", type: "writing", answer: "Student's name", marks: 2 },
              { id: 20, question: "Draw a happy face", type: "drawing", answer: "Happy face drawn", marks: 2 }
            ]
          }
        ],
        answerKey: {
          sectionA: ["A", "B", "C", "D", "E", "a", "b", "c", "a", "b"],
          sectionB: ["A", "B", "C", "D", "E"],
          sectionC: ["Circle drawn", "Square drawn", "A", "Name written", "Happy face"]
        }
      },
      term2: {
        id: "y1-lang-t2",
        title: "Year 1 Language & Communication - Term 2 Test",
        subject: "Language and Communication",
        totalMarks: 40,
        duration: "35 minutes",
        instructions: [
          "Write your name on the paper.",
          "Read each question carefully.",
          "Circle or write your answers."
        ],
        sections: [
          {
            name: "Section A: Letter Recognition (F-M)",
            marks: 16,
            instructions: "Circle the correct letter.",
            questions: [
              { id: 1, question: "Circle the letter F", options: ["E", "F", "T"], answer: "F", marks: 2 },
              { id: 2, question: "Circle the letter G", options: ["C", "G", "Q"], answer: "G", marks: 2 },
              { id: 3, question: "Circle the letter H", options: ["H", "N", "M"], answer: "H", marks: 2 },
              { id: 4, question: "Circle the letter I", options: ["L", "I", "T"], answer: "I", marks: 2 },
              { id: 5, question: "Circle the letter J", options: ["J", "L", "I"], answer: "J", marks: 2 },
              { id: 6, question: "Circle the letter K", options: ["X", "K", "Y"], answer: "K", marks: 2 },
              { id: 7, question: "Circle the letter L", options: ["I", "T", "L"], answer: "L", marks: 2 },
              { id: 8, question: "Circle the letter M", options: ["N", "W", "M"], answer: "M", marks: 2 }
            ]
          },
          {
            name: "Section B: Word Reading",
            marks: 12,
            instructions: "Read the word and match to the picture.",
            questions: [
              { id: 9, question: "CAT matches with", options: ["🐕", "🐱", "🐟"], answer: "🐱", marks: 3 },
              { id: 10, question: "DOG matches with", options: ["🐱", "🐟", "🐕"], answer: "🐕", marks: 3 },
              { id: 11, question: "SUN matches with", options: ["🌙", "☀️", "⭐"], answer: "☀️", marks: 3 },
              { id: 12, question: "HAT matches with", options: ["👒", "👟", "👓"], answer: "👒", marks: 3 }
            ]
          },
          {
            name: "Section C: Writing",
            marks: 12,
            instructions: "Write the word shown in the picture.",
            questions: [
              { id: 13, question: "Write the word: 🐱", type: "writing", answer: "CAT", marks: 3 },
              { id: 14, question: "Write the word: 🐕", type: "writing", answer: "DOG", marks: 3 },
              { id: 15, question: "Write the word: ☀️", type: "writing", answer: "SUN", marks: 3 },
              { id: 16, question: "Write your name", type: "writing", answer: "Student's name", marks: 3 }
            ]
          }
        ],
        answerKey: {
          sectionA: ["F", "G", "H", "I", "J", "K", "L", "M"],
          sectionB: ["🐱", "🐕", "☀️", "👒"],
          sectionC: ["CAT", "DOG", "SUN", "Name"]
        }
      }
    },
    mathematics: {
      term1: {
        id: "y1-math-t1",
        title: "Year 1 Mathematics - Term 1 Test",
        subject: "Mathematics",
        totalMarks: 30,
        duration: "30 minutes",
        instructions: [
          "Write your name.",
          "Count carefully.",
          "Write numbers clearly."
        ],
        sections: [
          {
            name: "Section A: Counting (1-10)",
            marks: 10,
            instructions: "Count the objects and write the number.",
            questions: [
              { id: 1, question: "Count: ⭐⭐⭐", type: "counting", answer: "3", marks: 2 },
              { id: 2, question: "Count: 🍎🍎🍎🍎🍎", type: "counting", answer: "5", marks: 2 },
              { id: 3, question: "Count: 🐟🐟🐟🐟🐟🐟🐟", type: "counting", answer: "7", marks: 2 },
              { id: 4, question: "Count: ⚽⚽⚽⚽⚽⚽⚽⚽⚽⚽", type: "counting", answer: "10", marks: 2 },
              { id: 5, question: "Count: 🌺🌺", type: "counting", answer: "2", marks: 2 }
            ]
          },
          {
            name: "Section B: Number Recognition",
            marks: 10,
            instructions: "Circle the correct number.",
            questions: [
              { id: 6, question: "Circle 5", options: ["3", "5", "8"], answer: "5", marks: 2 },
              { id: 7, question: "Circle 7", options: ["7", "1", "4"], answer: "7", marks: 2 },
              { id: 8, question: "Circle 10", options: ["01", "10", "100"], answer: "10", marks: 2 },
              { id: 9, question: "What comes after 4?", options: ["3", "5", "6"], answer: "5", marks: 2 },
              { id: 10, question: "What comes before 8?", options: ["9", "7", "6"], answer: "7", marks: 2 }
            ]
          },
          {
            name: "Section C: Shapes",
            marks: 10,
            instructions: "Name or identify the shape.",
            questions: [
              { id: 11, question: "⬜ This shape is called a", options: ["circle", "square", "triangle"], answer: "square", marks: 2 },
              { id: 12, question: "⭕ This shape is called a", options: ["circle", "square", "triangle"], answer: "circle", marks: 2 },
              { id: 13, question: "🔺 This shape is called a", options: ["circle", "square", "triangle"], answer: "triangle", marks: 2 },
              { id: 14, question: "How many sides does a square have?", options: ["3", "4", "5"], answer: "4", marks: 2 },
              { id: 15, question: "How many sides does a triangle have?", options: ["3", "4", "5"], answer: "3", marks: 2 }
            ]
          }
        ],
        answerKey: {
          sectionA: ["3", "5", "7", "10", "2"],
          sectionB: ["5", "7", "10", "5", "7"],
          sectionC: ["square", "circle", "triangle", "4", "3"]
        }
      }
    }
  },
  "Year 2": {
    languageCommunication: {
      term1: {
        id: "y2-lang-t1",
        title: "Year 2 Language & Communication - Term 1 Test",
        subject: "Language and Communication",
        totalMarks: 50,
        duration: "40 minutes",
        instructions: [
          "Write your name clearly.",
          "Read each question carefully.",
          "Answer all questions."
        ],
        sections: [
          {
            name: "Section A: Reading Comprehension",
            marks: 20,
            passage: "Tom has a dog. The dog is brown. The dog likes to run. Tom and his dog play in the garden every day.",
            instructions: "Read the passage and answer the questions.",
            questions: [
              { id: 1, question: "Who has a dog?", options: ["Mary", "Tom", "Sam"], answer: "Tom", marks: 4 },
              { id: 2, question: "What color is the dog?", options: ["black", "white", "brown"], answer: "brown", marks: 4 },
              { id: 3, question: "What does the dog like to do?", options: ["sleep", "run", "eat"], answer: "run", marks: 4 },
              { id: 4, question: "Where do they play?", options: ["in the house", "in the garden", "at school"], answer: "in the garden", marks: 4 },
              { id: 5, question: "How often do they play?", options: ["sometimes", "never", "every day"], answer: "every day", marks: 4 }
            ]
          },
          {
            name: "Section B: Spelling",
            marks: 15,
            instructions: "Write the correct spelling.",
            questions: [
              { id: 6, question: "Spell the word for: 🏠", type: "spelling", answer: "HOUSE", marks: 3 },
              { id: 7, question: "Spell the word for: 🌳", type: "spelling", answer: "TREE", marks: 3 },
              { id: 8, question: "Spell the word for: 📖", type: "spelling", answer: "BOOK", marks: 3 },
              { id: 9, question: "Spell the word for: 🐟", type: "spelling", answer: "FISH", marks: 3 },
              { id: 10, question: "Spell the word for: 🌺", type: "spelling", answer: "FLOWER", marks: 3 }
            ]
          },
          {
            name: "Section C: Sentence Writing",
            marks: 15,
            instructions: "Write a complete sentence.",
            questions: [
              { id: 11, question: "Write a sentence about your family", type: "writing", answer: "Varies - complete sentence", marks: 5 },
              { id: 12, question: "Write a sentence about your school", type: "writing", answer: "Varies - complete sentence", marks: 5 },
              { id: 13, question: "Write a sentence about your friend", type: "writing", answer: "Varies - complete sentence", marks: 5 }
            ]
          }
        ],
        answerKey: {
          sectionA: ["Tom", "brown", "run", "in the garden", "every day"],
          sectionB: ["HOUSE", "TREE", "BOOK", "FISH", "FLOWER"],
          sectionC: ["Complete sentence about family", "Complete sentence about school", "Complete sentence about friend"]
        }
      }
    },
    mathematics: {
      term1: {
        id: "y2-math-t1",
        title: "Year 2 Mathematics - Term 1 Test",
        subject: "Mathematics",
        totalMarks: 50,
        duration: "40 minutes",
        instructions: [
          "Write your name.",
          "Show your working.",
          "Check your answers."
        ],
        sections: [
          {
            name: "Section A: Addition",
            marks: 20,
            instructions: "Solve the addition problems.",
            questions: [
              { id: 1, question: "23 + 14 = ", type: "calculation", answer: "37", marks: 4 },
              { id: 2, question: "35 + 22 = ", type: "calculation", answer: "57", marks: 4 },
              { id: 3, question: "41 + 28 = ", type: "calculation", answer: "69", marks: 4 },
              { id: 4, question: "52 + 36 = ", type: "calculation", answer: "88", marks: 4 },
              { id: 5, question: "64 + 25 = ", type: "calculation", answer: "89", marks: 4 }
            ]
          },
          {
            name: "Section B: Subtraction",
            marks: 20,
            instructions: "Solve the subtraction problems.",
            questions: [
              { id: 6, question: "45 - 23 = ", type: "calculation", answer: "22", marks: 4 },
              { id: 7, question: "58 - 34 = ", type: "calculation", answer: "24", marks: 4 },
              { id: 8, question: "67 - 45 = ", type: "calculation", answer: "22", marks: 4 },
              { id: 9, question: "89 - 56 = ", type: "calculation", answer: "33", marks: 4 },
              { id: 10, question: "74 - 31 = ", type: "calculation", answer: "43", marks: 4 }
            ]
          },
          {
            name: "Section C: Word Problems",
            marks: 10,
            instructions: "Read and solve.",
            questions: [
              { id: 11, question: "Mary has 15 mangoes. She gets 12 more. How many does she have now?", type: "word_problem", answer: "27", marks: 5 },
              { id: 12, question: "Tom has 38 shells. He gives 15 to his friend. How many are left?", type: "word_problem", answer: "23", marks: 5 }
            ]
          }
        ],
        answerKey: {
          sectionA: ["37", "57", "69", "88", "89"],
          sectionB: ["22", "24", "22", "33", "43"],
          sectionC: ["27", "23"]
        }
      }
    }
  },
  "Year 3": {
    languageCommunication: {
      term1: {
        id: "y3-lang-t1",
        title: "Year 3 Language & Communication - Term 1 Test",
        subject: "Language and Communication",
        totalMarks: 60,
        duration: "45 minutes",
        instructions: [
          "Write your name clearly at the top.",
          "Read all instructions carefully.",
          "Answer in full sentences where required."
        ],
        sections: [
          {
            name: "Section A: English Vocabulary",
            marks: 20,
            instructions: "Match the English word to its meaning or picture.",
            questions: [
              { id: 1, question: "CAT means", options: ["pusi", "dok", "bus"], answer: "pusi", marks: 2 },
              { id: 2, question: "DOG means", options: ["pusi", "dok", "bus"], answer: "dok", marks: 2 },
              { id: 3, question: "HOUSE means", options: ["skul", "haos", "maket"], answer: "haos", marks: 2 },
              { id: 4, question: "SCHOOL means", options: ["skul", "haos", "maket"], answer: "skul", marks: 2 },
              { id: 5, question: "WATER means", options: ["wota", "kakae", "bus"], answer: "wota", marks: 2 },
              { id: 6, question: "FOOD means", options: ["wota", "kakae", "trak"], answer: "kakae", marks: 2 },
              { id: 7, question: "'Good morning' means", options: ["Gud naet", "Gud moning", "Gud aftenun"], answer: "Gud moning", marks: 2 },
              { id: 8, question: "'Thank you' means", options: ["Sori", "Tangkiu", "Plis"], answer: "Tangkiu", marks: 2 },
              { id: 9, question: "BOOK means", options: ["pen", "buk", "pepa"], answer: "buk", marks: 2 },
              { id: 10, question: "TREE means", options: ["graon", "tri", "flaoa"], answer: "tri", marks: 2 }
            ]
          },
          {
            name: "Section B: Reading Comprehension",
            marks: 20,
            passage: "My name is Sarah. I am 8 years old. I go to Quatuineala Primary School. I like to read books and play with my friends. My favorite subject is Mathematics because I like numbers.",
            instructions: "Read the passage and answer in English.",
            questions: [
              { id: 11, question: "What is the girl's name?", type: "short_answer", answer: "Sarah", marks: 4 },
              { id: 12, question: "How old is she?", type: "short_answer", answer: "8 years old", marks: 4 },
              { id: 13, question: "What school does she go to?", type: "short_answer", answer: "Quatuineala Primary School", marks: 4 },
              { id: 14, question: "What does she like to do?", type: "short_answer", answer: "Read books and play with friends", marks: 4 },
              { id: 15, question: "What is her favorite subject?", type: "short_answer", answer: "Mathematics", marks: 4 }
            ]
          },
          {
            name: "Section C: Writing in English",
            marks: 20,
            instructions: "Write in English.",
            questions: [
              { id: 16, question: "Write 3 sentences about yourself in English", type: "paragraph", answer: "3 complete sentences about self", marks: 10 },
              { id: 17, question: "Write 2 sentences about your family in English", type: "paragraph", answer: "2 complete sentences about family", marks: 10 }
            ]
          }
        ],
        answerKey: {
          sectionA: ["pusi", "dok", "haos", "skul", "wota", "kakae", "Gud moning", "Tangkiu", "buk", "tri"],
          sectionB: ["Sarah", "8 years old", "Quatuineala Primary School", "Read books and play with friends", "Mathematics"],
          sectionC: ["Self-description in English", "Family description in English"]
        }
      }
    },
    mathematics: {
      term1: {
        id: "y3-math-t1",
        title: "Year 3 Mathematics - Term 1 Test",
        subject: "Mathematics",
        totalMarks: 60,
        duration: "45 minutes",
        instructions: [
          "Write your name.",
          "Show all working.",
          "Write answers clearly."
        ],
        sections: [
          {
            name: "Section A: Place Value",
            marks: 20,
            instructions: "Answer the place value questions.",
            questions: [
              { id: 1, question: "Write 'three hundred forty-two' as a number", type: "number_writing", answer: "342", marks: 4 },
              { id: 2, question: "Write 'four hundred fifty-six' as a number", type: "number_writing", answer: "456", marks: 4 },
              { id: 3, question: "In 385, what is the value of 8?", options: ["8", "80", "800"], answer: "80", marks: 4 },
              { id: 4, question: "In 472, what is the value of 4?", options: ["4", "40", "400"], answer: "400", marks: 4 },
              { id: 5, question: "Which is greater: 356 or 365?", options: ["356", "365", "same"], answer: "365", marks: 4 }
            ]
          },
          {
            name: "Section B: Addition with Three-Digit Numbers",
            marks: 20,
            instructions: "Solve the addition problems. Show your working.",
            questions: [
              { id: 6, question: "234 + 152 = ", type: "calculation", answer: "386", marks: 4 },
              { id: 7, question: "345 + 231 = ", type: "calculation", answer: "576", marks: 4 },
              { id: 8, question: "412 + 285 = ", type: "calculation", answer: "697", marks: 4 },
              { id: 9, question: "256 + 143 = ", type: "calculation", answer: "399", marks: 4 },
              { id: 10, question: "378 + 121 = ", type: "calculation", answer: "499", marks: 4 }
            ]
          },
          {
            name: "Section C: Word Problems",
            marks: 20,
            instructions: "Read carefully and solve. Show your working.",
            questions: [
              { id: 11, question: "A shop has 245 mangoes. They receive 134 more. How many mangoes are there now?", type: "word_problem", answer: "379", marks: 5 },
              { id: 12, question: "There are 186 students in Year 3 and 192 students in Year 4. How many students altogether?", type: "word_problem", answer: "378", marks: 5 },
              { id: 13, question: "Father has 350 vatu. He earns 125 vatu more. How much does he have now?", type: "word_problem", answer: "475 vatu", marks: 5 },
              { id: 14, question: "A farmer has 278 coconuts. He picks 121 more. How many coconuts does he have?", type: "word_problem", answer: "399", marks: 5 }
            ]
          }
        ],
        answerKey: {
          sectionA: ["342", "456", "80", "400", "365"],
          sectionB: ["386", "576", "697", "399", "499"],
          sectionC: ["379", "378", "475 vatu", "399"]
        }
      }
    }
  },
  ...testPapersYears4to6
};

export default primaryCurriculum;
